import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatLocalDate } from "@/lib/date";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CATEGORIES as INTEREST_CATEGORIES, PARTICIPATION_ROLES, OPTION_LABEL_BY_KEY, CATEGORY_LABEL_BY_KEY, PARTICIPATION_LABEL_BY_KEY } from "@/lib/communityCategories";
import { toast } from "sonner";
import {
  Users, Bell, Calendar, DollarSign, Download, Repeat, Home,
  AlertTriangle, Plus, Upload, Trash2, Edit, MailWarning, Receipt,
  Shield, Save, RefreshCw, ArrowUpDown, CheckCircle2, XCircle, Clock, Pin, Pencil, CalendarClock, FileText, Link2,
  ChevronsUpDown, Check, UserPlus, Search, Paperclip, PartyPopper, HandHeart, Lightbulb, Star, Sparkles,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STANDARD_METHODS = new Set(["check", "cash", "bank_transfer", "money_order"]);

/**
 * Searchable property combobox — typeahead over address / unit # / owner name / email.
 * Falls back to a plain button trigger showing the current selection or a "Choose unit…" placeholder.
 */
function PropertyPicker({ properties, value, onChange, testidPrefix = "property-picker", placeholder = "Choose unit…" }) {
  const [open, setOpen] = useState(false);
  const current = properties.find((p) => p.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testidPrefix}-trigger`}
          className="mt-1 w-full flex items-center justify-between rounded-xl border border-input bg-white px-3 h-10 text-sm text-left hover:border-[#C86650]"
        >
          <span className={current ? "text-[#1A2421]" : "text-[#5C6A64]"}>
            {current ? `${current.address} — Unit ${current.unit_number} · ${current.owner_name}` : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-w-[520px]" align="start">
        <Command>
          <CommandInput data-testid={`${testidPrefix}-search`} placeholder="Search address, unit, owner…" />
          <CommandList>
            <CommandEmpty>No property matches.</CommandEmpty>
            <CommandGroup>
              {properties.map((p) => {
                const searchText = `${p.address} ${p.unit_number} ${p.owner_name || ""} ${p.owner_email || ""}`.toLowerCase();
                return (
                  <CommandItem
                    key={p.id}
                    value={searchText}
                    onSelect={() => { onChange(p.id); setOpen(false); }}
                    data-testid={`${testidPrefix}-option-${p.id}`}
                    className="cursor-pointer"
                  >
                    <Check className={`mr-2 h-4 w-4 ${value === p.id ? "opacity-100 text-[#C86650]" : "opacity-0"}`} />
                    <div className="flex-1">
                      <div className="text-sm text-[#1A2421]">{p.address} · Unit {p.unit_number}</div>
                      <div className="text-xs text-[#5C6A64]">{p.owner_name}{p.owner_email ? ` · ${p.owner_email}` : ""}</div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function AdminPage() {
  const [stats, setStats] = useState({});
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [delinquency, setDelinquency] = useState({ properties: [], total_outstanding: 0, delinquent_count: 0, partial_count: 0 });
  const [amenities, setAmenities] = useState([]);
  const [users, setUsers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [paymentSort, setPaymentSort] = useState({ key: "created_at", dir: "desc" });
  const [paymentSearch, setPaymentSearch] = useState("");
  const [selectedPayments, setSelectedPayments] = useState(() => new Set()); // set of payment ids selected for bulk delete
  const [linkingPayment, setLinkingPayment] = useState(null); // payment being linked to a property
  const [syncing, setSyncing] = useState(false);
  const [msgFilter, setMsgFilter] = useState("inbox");
  const [bookingFilter, setBookingFilter] = useState("active"); // active | archived | all
  const [editingBooking, setEditingBooking] = useState(null);
  const [adminTab, setAdminTab] = useState("properties");
  const [acceptProposal, setAcceptProposal] = useState(null); // { property, preset } | null

  const load = async () => {
    try {
      const [s, b, m, p, pr, dq, am, us, an] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/bookings"),
        api.get("/contact/messages"),
        api.get("/admin/payments"),
        api.get("/admin/properties"),
        api.get("/admin/delinquency"),
        api.get("/admin/amenities"),
        api.get("/admin/users"),
        api.get("/admin/announcements"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setMessages(m.data);
      setPayments(p.data);
      setProperties(pr.data);
      setDelinquency(dq.data);
      setAmenities(am.data);
      setUsers(us.data);
      setAnnouncements(an.data);
    } catch (e) {
      toast.error("Failed to load admin data");
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    let admin_notes = null;
    if (status === "confirmed" || status === "rejected") {
      const label = status === "confirmed" ? "approval" : "rejection";
      admin_notes = window.prompt(`Optional note for ${label} (sent to the homeowner). Leave blank to skip.`) || null;
    }
    try {
      await api.patch(`/bookings/${id}`, { status, admin_notes });
      toast.success(`Booking ${status}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const patchMessage = async (id, fields) => {
    try { await api.patch(`/contact/messages/${id}`, fields); load(); }
    catch { toast.error("Failed"); }
  };
  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    try { await api.delete(`/contact/messages/${id}`); toast.success("Message deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const visibleMessages = messages.filter((m) => {
    if (msgFilter === "archived") return m.archived === true;
    if (msgFilter === "requests") return !m.archived && (m.kind === "propose_plan" || m.kind === "contact_me" || m.kind === "balance_dispute" || m.has_new_homeowner_reply);
    if (msgFilter === "inbox") return m.archived !== true;
    return true;
  });
  const pendingRequests = messages.filter((m) => !m.archived && (m.kind === "propose_plan" || m.kind === "contact_me" || m.kind === "balance_dispute" || m.has_new_homeowner_reply)).length;

  const filteredPayments = payments.filter((p) => {
    if (paymentFilter === "paid" && p.payment_status !== "paid") return false;
    if (paymentFilter === "pending" && p.payment_status !== "pending") return false;
    if (paymentFilter === "subscription" && !(p.mode === "subscription" || p.mode === "subscription_renewal")) return false;
    if (paymentFilter === "manual" && p.mode !== "manual") return false;
    if (paymentFilter === "test_mode" && !String(p.session_id || "").startsWith("cs_test_")) return false;
    if (paymentFilter === "legacy_stripe") {
      // Legacy Stripe rows: has a PaymentIntent but no session_id → likely from before we tracked test/live mode.
      // These are the "unknown provenance" rows admins should audit against Stripe Dashboard.
      const looksLikeStripe = (p.mode === "payment" || p.mode === "subscription" || p.mode === "subscription_renewal");
      if (!(looksLikeStripe && p.stripe_payment_intent_id && !p.session_id)) return false;
    }
    const q = paymentSearch.trim().toLowerCase();
    if (q) {
      const hay = `${p.user_name || ""} ${p.user_email || ""} ${p.reference || ""} ${p.lookup_key || ""} ${p.method || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Human-readable form-of-payment for a transaction (used in the Method column).
  const paymentMethodLabel = (p) => {
    const m = p.method || "";
    const mode = p.mode || "";
    if (m === "settlement_option1") return "Settlement · Option 1 (65% Off)";
    if (m === "settlement_option2") return "Settlement · Option 2 (50% Off Plan)";
    if (m === "settlement_credit" || p.lookup_key === "settlement_credit") return "Settlement credit (board applied)";
    if (m === "check") return "Check" + (p.check_number ? ` #${p.check_number}` : "");
    if (m === "cash") return "Cash";
    if (m === "money_order") return "Money order";
    if (m === "bank_transfer") return "Bank transfer";
    if (m && !["credit_card", "card"].includes(m)) return m.replace(/_/g, " ");
    if (mode === "payment" || mode === "subscription" || mode === "subscription_renewal") return "Credit card · Stripe";
    if (mode === "manual") return "Manual entry";
    return mode || "—";
  };

  // Apply the currently-selected sort to filtered payments.
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    const dir = paymentSort.dir === "asc" ? 1 : -1;
    switch (paymentSort.key) {
      case "created_at": return dir * (new Date(a.created_at) - new Date(b.created_at));
      case "member": return dir * (a.user_name || a.user_email || "").localeCompare(b.user_name || b.user_email || "");
      case "property": return dir * (a.property_address || "\uffff").localeCompare(b.property_address || "\uffff");
      case "method": return dir * paymentMethodLabel(a).localeCompare(paymentMethodLabel(b));
      case "amount": return dir * ((a.amount || 0) - (b.amount || 0));
      case "status": return dir * (a.payment_status || "").localeCompare(b.payment_status || "");
      default: return 0;
    }
  });

  const toggleSort = (key) => setPaymentSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const SortArrow = ({ col }) => paymentSort.key === col
    ? <span className="ml-1 text-[10px]" data-testid={`sort-arrow-${col}`}>{paymentSort.dir === "asc" ? "▲" : "▼"}</span>
    : <ArrowUpDown className="w-3 h-3 inline-block ml-1 opacity-40" />;

  const totalRevenue = (stats.total_revenue || 0) / 100;

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-6 py-12">
      <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Board</p>
      <h1 className="font-serif text-4xl text-[#1A2421] mb-10">Admin Console</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
        {[
          { icon: Home, label: "Properties", value: properties.length },
          { icon: Users, label: "Members", value: stats.users || 0 },
          { icon: DollarSign, label: "Revenue", value: `$${totalRevenue.toLocaleString()}` },
          { icon: AlertTriangle, label: "Outstanding", value: `$${(delinquency.total_outstanding || 0).toLocaleString()}`, alert: delinquency.total_outstanding > 0 },
          { icon: Calendar, label: "Pending", value: stats.pending_bookings || 0 },
          { icon: Repeat, label: "Auto-pay", value: stats.active_subscriptions || 0 },
        ].map((s) => (
          <Card key={s.label} data-testid={`stat-${s.label.toLowerCase()}`} className={`rounded-2xl border-[#D5CFC4] ${s.alert ? "bg-[#FDF3F0] border-[#C86650]" : ""}`}>
            <CardContent className="p-5">
              <s.icon className={`w-5 h-5 mb-2 ${s.alert ? "text-[#C86650]" : "text-[#C86650]"}`} />
              <p className="font-serif text-2xl text-[#2C4C3B]">{s.value}</p>
              <p className="text-xs text-[#5C6A64] uppercase tracking-wider mt-1">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={adminTab} onValueChange={setAdminTab}>
        <TabsList className="bg-[#EADCC8] flex-wrap h-auto">
          <TabsTrigger data-testid="admin-tab-properties" value="properties"><Home className="w-4 h-4 mr-2" />Properties</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-delinquency" value="delinquency">
            <AlertTriangle className="w-4 h-4 mr-2" />Delinquency
            {delinquency.properties.length > 0 && <Badge className="ml-2 bg-[#C86650]">{delinquency.properties.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-payments" value="payments"><Receipt className="w-4 h-4 mr-2" />Payments</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-arrangements" value="arrangements"><CalendarClock className="w-4 h-4 mr-2" />Arrangements</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-announcements" value="announcements">
            <Bell className="w-4 h-4 mr-2" />Announcements
            {announcements.filter((a) => a.status === "pending").length > 0 && (
              <Badge className="ml-2 bg-[#C86650]">{announcements.filter((a) => a.status === "pending").length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger data-testid="admin-tab-amenities" value="amenities">Amenities</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-documents" value="documents"><FileText className="w-4 h-4 mr-2" />Documents</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-board" value="board"><Shield className="w-4 h-4 mr-2" />Board</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-bookings" value="bookings">Bookings</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-volunteers" value="volunteers"><PartyPopper className="w-4 h-4 mr-2" />Volunteers</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-interests" value="interests"><HandHeart className="w-4 h-4 mr-2" />Get Involved</TabsTrigger>
          <TabsTrigger data-testid="admin-tab-messages" value="messages">
            Messages
            {pendingRequests > 0 && <Badge className="ml-2 bg-[#C86650]">{pendingRequests}</Badge>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="properties">
          <PropertiesTab properties={properties} onReload={load} />
        </TabsContent>

        <TabsContent value="delinquency">
          <DelinquencyTab data={delinquency} properties={properties} onReload={load} />
        </TabsContent>

        <TabsContent value="arrangements">
          <ArrangementsTab properties={properties} onReload={load} />
        </TabsContent>

        <TabsContent value="payments">
          <div className="flex justify-between items-center mt-4 mb-4 gap-3 flex-wrap">
            <div className="flex gap-2 items-center flex-wrap">
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger data-testid="payment-filter-select" className="w-56 rounded-xl bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All transactions</SelectItem>
                  <SelectItem value="paid">Paid only</SelectItem>
                  <SelectItem value="pending">Pending only</SelectItem>
                  <SelectItem value="subscription">Subscriptions only</SelectItem>
                  <SelectItem value="manual">Check / cash / offline</SelectItem>
                  <SelectItem value="test_mode" data-testid="filter-test-mode">Stripe TEST-mode (fake money) ⚠️</SelectItem>
                  <SelectItem value="legacy_stripe" data-testid="filter-legacy-stripe">Legacy Stripe rows (verify in dashboard)</SelectItem>
                </SelectContent>
              </Select>
              <Input
                data-testid="payment-search"
                value={paymentSearch}
                onChange={(e) => setPaymentSearch(e.target.value)}
                placeholder="Search name, email, reference…"
                className="w-64 rounded-xl bg-white"
              />
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="sync-stripe-btn"
                variant="outline"
                className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white"
                disabled={syncing}
                onClick={async () => {
                  setSyncing(true);
                  try {
                    const r = await api.post("/admin/payments/sync");
                    toast.success(`Synced: ${r.data.reconciled} updated, ${r.data.imported} imported`);
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
                  finally { setSyncing(false); }
                }}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync with Stripe"}
              </Button>
              <ManualPaymentDialog properties={properties} onDone={load} />
              <ReconciliationPdfButton />
              <Button
                data-testid="cleanup-pending-btn"
                variant="outline"
                className="rounded-full border-[#C86650] text-[#C86650] hover:bg-[#C86650] hover:text-white"
                onClick={async () => {
                  if (!window.confirm("Cancel EVERY Stripe payment currently marked pending? These are checkouts that were started but never confirmed by Stripe — no real money was involved. Manual entries (check / cash / money order) will NOT be touched.")) return;
                  try {
                    const r = await api.post("/admin/payments/cleanup-pending?older_than_hours=0");
                    toast.success(`Canceled ${r.data.canceled} pending Stripe row${r.data.canceled === 1 ? "" : "s"}`);
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Cleanup failed"); }
                }}
              >
                <XCircle className="w-4 h-4 mr-2" /> Cancel all pending
              </Button>
              <Button
                data-testid="rollover-year-btn"
                variant="outline"
                className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white"
                onClick={async () => {
                  if (!window.confirm("Roll last year's overpayments forward now?\n\nFor every property that overpaid last year, this will store the excess as a credit that automatically reduces this year's dues. Already runs automatically on Jan 1 — use this only when you want to force it early or replay after fixing prior-year data.\n\nThis is idempotent (safe to click again — a property that's already been rolled forward for this year won't change).")) return;
                  try {
                    const r = await api.post("/admin/properties/rollover-year");
                    toast.success(`Rolled ${r.data.properties_rolled_forward} property credit${r.data.properties_rolled_forward === 1 ? "" : "s"} forward`);
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Rollover failed"); }
                }}
              >
                <Repeat className="w-4 h-4 mr-2" /> Roll credits forward
              </Button>
              <a href={`${BACKEND}/api/admin/payments/export`} data-testid="export-csv-btn" target="_blank" rel="noreferrer">
                <Button variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white">
                  <Download className="w-4 h-4 mr-2" /> Export CSV
                </Button>
              </a>
            </div>
          </div>

          {paymentFilter === "test_mode" && (
            <div data-testid="test-mode-banner" className="mb-4 p-4 rounded-xl bg-[#FDF4F1] border-2 border-[#C86650]">
              <p className="text-sm text-[#1A2421] font-medium mb-1">⚠️ Stripe TEST-mode transactions ({filteredPayments.length})</p>
              <p className="text-xs text-[#5C6A64]">
                These payments were processed while the site was pointing at Stripe test keys — <b>no real money was charged</b> and none of it landed in your live Stripe account. Test cards like <code>4242 4242 4242 4242</code> would have "succeeded" here.
                Select all + Delete to remove them from your ledger so homeowner balances reset to what they actually owe. Real live payments (<code>cs_live_…</code>) are unaffected.
              </p>
              {filteredPayments.length > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="select-all-test-btn"
                  className="mt-3 rounded-full border-[#C86650] text-[#C86650] hover:bg-[#C86650] hover:text-white"
                  onClick={() => {
                    const next = new Set(selectedPayments);
                    filteredPayments.forEach((p) => next.add(p.id));
                    setSelectedPayments(next);
                  }}
                >
                  Select all {filteredPayments.length} test-mode transactions
                </Button>
              )}
            </div>
          )}

          {selectedPayments.size > 0 && (
            <div data-testid="bulk-delete-bar" className="mb-4 flex items-center gap-3 p-3 rounded-xl bg-[#FDF4F1] border-2 border-[#C86650]">
              <p className="text-sm text-[#1A2421] flex-1"><b>{selectedPayments.size}</b> transaction{selectedPayments.size === 1 ? "" : "s"} selected</p>
              <Button
                variant="outline"
                size="sm"
                data-testid="bulk-clear-btn"
                className="rounded-full"
                onClick={() => setSelectedPayments(new Set())}
              >
                Clear
              </Button>
              <Button
                size="sm"
                data-testid="bulk-delete-btn"
                className="rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white"
                onClick={async () => {
                  const n = selectedPayments.size;
                  if (!window.confirm(`Delete ${n} selected transaction${n === 1 ? "" : "s"}? This cannot be undone.`)) return;
                  try {
                    const r = await api.post("/admin/payments/bulk-delete", { ids: Array.from(selectedPayments) });
                    toast.success(`${r.data.deleted_count} transaction${r.data.deleted_count === 1 ? "" : "s"} deleted`);
                    setSelectedPayments(new Set());
                    load();
                  } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
                }}
              >
                <Trash2 className="w-3 h-3 mr-1" /> Delete {selectedPayments.size} selected
              </Button>
            </div>
          )}

          <Card className="rounded-2xl border-[#D5CFC4]">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#EADCC8] text-[#2C4C3B]">
                  <tr>
                    <th className="text-left px-4 py-3 w-8">
                      <input
                        type="checkbox"
                        data-testid="select-all-payments"
                        checked={filteredPayments.length > 0 && filteredPayments.every((p) => selectedPayments.has(p.id))}
                        onChange={(e) => {
                          const next = new Set(selectedPayments);
                          if (e.target.checked) filteredPayments.forEach((p) => next.add(p.id));
                          else filteredPayments.forEach((p) => next.delete(p.id));
                          setSelectedPayments(next);
                        }}
                        className="h-4 w-4 rounded border-[#2C4C3B] accent-[#2C4C3B]"
                      />
                    </th>
                    <th className="text-left px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("created_at")} data-testid="sort-date"><span className="inline-flex items-center">Date<SortArrow col="created_at" /></span></th>
                    <th className="text-left px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("member")} data-testid="sort-member"><span className="inline-flex items-center">Member<SortArrow col="member" /></span></th>
                    <th className="text-left px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("property")} data-testid="sort-property"><span className="inline-flex items-center">Property<SortArrow col="property" /></span></th>
                    <th className="text-left px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("method")} data-testid="sort-method"><span className="inline-flex items-center">Method<SortArrow col="method" /></span></th>
                    <th className="text-left px-4 py-3">Plan / Ref</th>
                    <th className="text-left px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("amount")} data-testid="sort-amount"><span className="inline-flex items-center">Amount<SortArrow col="amount" /></span></th>
                    <th className="text-left px-4 py-3">Mode</th>
                    <th className="text-left px-4 py-3 cursor-pointer select-none" onClick={() => toggleSort("status")} data-testid="sort-status"><span className="inline-flex items-center">Status<SortArrow col="status" /></span></th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPayments.map((p) => (
                    <tr key={p.id} data-testid={`payment-row-${p.id}`} className={`border-t border-[#D5CFC4] hover:bg-[#F9F8F6] ${selectedPayments.has(p.id) ? "bg-[#FDF4F1]" : ""}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          data-testid={`select-payment-${p.id}`}
                          checked={selectedPayments.has(p.id)}
                          onChange={(e) => {
                            const next = new Set(selectedPayments);
                            if (e.target.checked) next.add(p.id); else next.delete(p.id);
                            setSelectedPayments(next);
                          }}
                          className="h-4 w-4 rounded border-[#2C4C3B] accent-[#2C4C3B]"
                        />
                      </td>
                      <td className="px-4 py-3 text-[#5C6A64] whitespace-nowrap" data-testid={`payment-date-${p.id}`}>
                        <div>{new Date(p.created_at).toLocaleDateString()}</div>
                        <div className="text-[10px] text-[#8A9490]">{new Date(p.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-[#1A2421] font-medium">{p.user_name || "—"}</p>
                        <p className="text-xs text-[#5C6A64]">{p.user_email}</p>
                      </td>
                      <td className="px-4 py-3">
                        {p.property_address ? (
                          <Link
                            to={`/admin/properties/${p.property_id}`}
                            data-testid={`payment-property-${p.id}`}
                            className="text-xs text-[#2C4C3B] font-medium inline-flex items-center hover:underline"
                          >
                            <Home className="w-3 h-3 inline mr-1" />{p.property_address}{p.property_unit_number ? <> · Unit {p.property_unit_number}</> : null}
                          </Link>
                        ) : (
                          <button
                            type="button"
                            data-testid={`link-property-btn-${p.id}`}
                            onClick={() => setLinkingPayment(p)}
                            className="text-[11px] text-[#C86650] italic hover:underline inline-flex items-center gap-1"
                          >
                            <Link2 className="w-3 h-3" /> No linked property — click to link
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-[#1A2421]" data-testid={`payment-method-${p.id}`}>{paymentMethodLabel(p)}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          {p.stripe_payment_intent_id && (
                            <p className="text-[10px] text-[#5C6A64]">{String(p.stripe_payment_intent_id).slice(0, 18)}…</p>
                          )}
                          {(() => {
                            const sid = String(p.session_id || "");
                            if (sid.startsWith("cs_test_")) {
                              return <span data-testid={`stripe-mode-${p.id}`} className="text-[9px] uppercase tracking-wider rounded-full bg-[#C86650] text-white px-1.5 py-0.5 font-medium">TEST · fake money</span>;
                            }
                            if (sid.startsWith("cs_live_")) {
                              return <span data-testid={`stripe-mode-${p.id}`} className="text-[9px] uppercase tracking-wider rounded-full border border-[#2C4C3B] text-[#2C4C3B] px-1.5 py-0.5 font-medium">LIVE</span>;
                            }
                            // Stripe-shaped row (has PI but no session_id) — pre-dates session tracking, ambiguous.
                            if (p.stripe_payment_intent_id && (p.mode === "payment" || p.mode === "subscription" || p.mode === "subscription_renewal")) {
                              return <span data-testid={`stripe-mode-${p.id}`} title="No session_id recorded — verify in Stripe Dashboard whether this landed in Test or Live mode." className="text-[9px] uppercase tracking-wider rounded-full bg-[#EADCC8] text-[#5C6A64] px-1.5 py-0.5 font-medium cursor-help">Legacy · verify in Stripe</span>;
                            }
                            return null;
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#1A2421]">
                        {(p.lookup_key || "").replace(/_/g, " ")}
                        {p.reference && <p className="text-xs text-[#5C6A64]">Ref: {p.reference}</p>}
                        {(() => {
                          const opt = p.settlement_option || (
                            p.method === "settlement_option1" ? "option1_65_paid_in_full" :
                            p.method === "settlement_option2" ? "option2_50_payment_plan" :
                            null
                          );
                          if (!opt) return null;
                          const label = opt === "option1_65_paid_in_full" ? "65% Off" :
                                        opt === "option2_50_payment_plan" ? "50% Off Plan" :
                                        "Custom";
                          const isCredit = p.lookup_key === "settlement_credit";
                          const isDeposit = p.lookup_key === "settlement_deposit";
                          const prefix = isCredit ? "Settlement Credit" : isDeposit ? "Settlement Deposit" : "Settlement";
                          return (
                            <Badge
                              data-testid={`settlement-option-badge-${p.id}`}
                              className="mt-1 bg-[#C86650] hover:bg-[#C86650] text-white text-[10px]"
                            >
                              {prefix} — {label}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td className={`px-4 py-3 font-medium ${p.lookup_key === "settlement_credit" ? "text-[#C86650]" : "text-[#2C4C3B]"}`} data-testid={`amount-${p.id}`}>{p.lookup_key === "settlement_credit" ? "–" : ""}${((p.amount || 0) / 100).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="border-[#D5CFC4]">
                          {p.mode === "manual" ? p.method?.replace(/_/g, " ") || "Manual" :
                           p.mode === "subscription_renewal" ? "Renewal" :
                           p.mode === "subscription" ? "Auto-pay" : "One-time"}
                        </Badge>
                        {(() => {
                          const pct = p.partial_pct ?? ({ partial_half: 50, partial_sixty_five: 65, partial_full: 100 })[p.lookup_key];
                          if (pct && pct < 100) {
                            return <Badge data-testid={`partial-badge-${p.id}`} className="ml-1 bg-[#EADCC8] text-[#2C4C3B]">{pct}%</Badge>;
                          }
                          return null;
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={
                          p.payment_status === "paid" ? "bg-[#2C4C3B]" :
                          p.payment_status === "pending" ? "bg-[#EADCC8] text-[#2C4C3B]" :
                          "bg-[#C86650]"
                        }>
                          {p.payment_status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          {p.receipt_url && (
                            <a
                              href={p.receipt_url}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`receipt-link-${p.id}`}
                              title="View Stripe receipt"
                              className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-[#EADCC8]"
                            >
                              <FileText className="w-4 h-4 text-[#C86650]" />
                            </a>
                          )}
                          {p.payment_status === "paid" && (p.stripe_payment_intent_id || p.stripe_invoice_id) && (
                            <Button
                              size="icon"
                              variant="ghost"
                              data-testid={`resend-receipt-${p.id}`}
                              title="Resend Stripe receipt"
                              onClick={async () => {
                                try {
                                  const r = await api.post(`/admin/payments/${p.id}/resend-receipt`);
                                  toast.success(`Receipt sent${r.data.email ? " to " + r.data.email : ""}`);
                                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                              }}
                            >
                              <MailWarning className="w-4 h-4 text-[#2C4C3B]" />
                            </Button>
                          )}
                          <EditMethodButton payment={p} onDone={load} />
                          <Button
                            size="icon"
                            variant="ghost"
                            data-testid={`delete-payment-${p.id}`}
                            onClick={async () => {
                              if (!window.confirm(`Delete this transaction (${p.user_name || p.user_email} · $${((p.amount || 0) / 100).toFixed(2)})? This cannot be undone.`)) return;
                              try {
                                await api.delete(`/admin/payments/${p.id}`);
                                toast.success("Transaction deleted");
                                load();
                              } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                            }}
                            title="Delete transaction"
                          >
                            <Trash2 className="w-4 h-4 text-[#C86650]" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {sortedPayments.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-8 text-[#5C6A64]">No transactions.</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Dialog open={!!linkingPayment} onOpenChange={(o) => { if (!o) setLinkingPayment(null); }}>
            <DialogContent className="bg-[#F5F1E8] max-w-md" data-testid="link-property-dialog">
              <DialogHeader>
                <DialogTitle className="text-[#1A2421]">Link payment to a property</DialogTitle>
              </DialogHeader>
              {linkingPayment && (
                <div className="space-y-3">
                  <div className="text-sm text-[#5C6A64]">
                    <p><b className="text-[#1A2421]">{linkingPayment.user_name || linkingPayment.user_email}</b></p>
                    <p>${((linkingPayment.amount || 0) / 100).toFixed(2)} · {new Date(linkingPayment.created_at).toLocaleDateString()}</p>
                  </div>
                  <Label className="text-[#1A2421] text-sm">Choose a property</Label>
                  <Select
                    onValueChange={async (property_id) => {
                      try {
                        await api.patch(`/admin/payments/${linkingPayment.id}/link-property`, { property_id });
                        toast.success("Payment linked to property");
                        setLinkingPayment(null);
                        load();
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Failed to link");
                      }
                    }}
                  >
                    <SelectTrigger data-testid="link-property-select" className="border-[#D5CFC4] bg-white"><SelectValue placeholder="Select property…" /></SelectTrigger>
                    <SelectContent>
                      {properties.map((prop) => (
                        <SelectItem key={prop.id} value={prop.id} data-testid={`link-option-${prop.id}`}>
                          {prop.address}{prop.unit_number ? ` · Unit ${prop.unit_number}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="bookings">
          <div className="flex justify-between items-center mt-4 mb-3 flex-wrap gap-3">
            <div className="flex gap-2 items-center flex-wrap">
              <Button size="sm" variant={bookingFilter === "active" ? "default" : "outline"} onClick={() => setBookingFilter("active")} className={bookingFilter === "active" ? "rounded-full bg-[#2C4C3B]" : "rounded-full border-[#D5CFC4]"} data-testid="booking-filter-active">Active ({bookings.filter((b) => !b.is_archived).length})</Button>
              <Button size="sm" variant={bookingFilter === "archived" ? "default" : "outline"} onClick={() => setBookingFilter("archived")} className={bookingFilter === "archived" ? "rounded-full bg-[#2C4C3B]" : "rounded-full border-[#D5CFC4]"} data-testid="booking-filter-archived">Archived ({bookings.filter((b) => b.is_archived).length})</Button>
              <Button size="sm" variant={bookingFilter === "all" ? "default" : "outline"} onClick={() => setBookingFilter("all")} className={bookingFilter === "all" ? "rounded-full bg-[#2C4C3B]" : "rounded-full border-[#D5CFC4]"} data-testid="booking-filter-all">All ({bookings.length})</Button>
              <p className="text-xs text-[#5C6A64] ml-2">Archived = rejected / cancelled / past-date</p>
            </div>
            <ManualBookingDialog properties={properties} amenities={amenities} onDone={load} />
          </div>
          <div className="mb-3 p-3 rounded-xl bg-[#FDF9F1] border border-[#EADCC8] text-xs text-[#1A2421] flex items-start gap-2" data-testid="admin-bookings-bypass-hint">
            <Shield className="w-3.5 h-3.5 text-[#C86650] mt-0.5 shrink-0" />
            <span>Admin bookings created here bypass the past-due balance check — you can reserve amenities on behalf of any homeowner even if they carry a balance.</span>
          </div>
          <div className="space-y-3">
            {(() => {
              const visible = bookings.filter((b) => {
                if (bookingFilter === "active") return !b.is_archived;
                if (bookingFilter === "archived") return b.is_archived;
                return true;
              });
              if (visible.length === 0) return <p className="text-[#5C6A64]">No bookings.</p>;
              return visible.map((b) => (
              <Card key={b.id} data-testid={`admin-booking-${b.id}`} className={`rounded-2xl border-[#D5CFC4] ${b.is_archived ? "opacity-70" : ""}`}>
                <CardContent className="p-5 flex flex-wrap justify-between items-center gap-4">
                  <div>
                    <div className="font-medium text-[#1A2421] flex items-center gap-2 flex-wrap">
                      <span>{b.amenity_name || b.amenity} · {formatLocalDate(b.date, { weekday: "short", month: "short", day: "numeric", year: "numeric" })} · {b.time_slot}</span>
                      {b.manual_entry && <Badge className="bg-[#EADCC8] text-[#2C4C3B]">Manual</Badge>}
                    </div>
                    <p className="text-sm text-[#5C6A64]">
                      {b.user_name} ({b.user_email})
                      {b.is_guest && <span className="ml-2 text-[#C86650]">· guest</span>}
                      {b.guests && ` · ${b.guests} est. guest${b.guests > 1 ? "s" : ""}`}
                      {b.price ? ` · $${b.price.toFixed(2)}` : ""}
                    </p>
                    <div className="flex gap-2 mt-1 flex-wrap items-center">
                      {b.deposit_paid ? (
                        b.deposit_refunded ? (
                          <Badge data-testid={`deposit-refunded-${b.id}`} className="bg-[#5C6A64]">Deposit refunded (${(b.deposit_amount || 0).toFixed(0)})</Badge>
                        ) : (
                          <Badge data-testid={`deposit-paid-${b.id}`} className="bg-[#2C4C3B]">Deposit paid (${(b.deposit_amount || 0).toFixed(0)})</Badge>
                        )
                      ) : (
                        <Badge data-testid={`deposit-unpaid-${b.id}`} variant="outline" className="border-[#C86650] text-[#C86650]">Deposit unpaid</Badge>
                      )}
                    </div>
                    {b.notes && <p className="text-xs text-[#5C6A64] mt-1">Homeowner note: {b.notes}</p>}
                    {b.admin_notes && <p className="text-xs text-[#2C4C3B] mt-1">Board note: {b.admin_notes}</p>}
                  </div>
                  <div className="flex gap-2 items-center flex-wrap justify-end">
                    <Badge className={b.status === "confirmed" ? "bg-[#2C4C3B]" : b.status === "pending" ? "bg-[#EADCC8] text-[#2C4C3B]" : "bg-[#C86650]"}>{b.status}</Badge>
                    {b.status === "pending" && (
                      <>
                        <Button size="sm" onClick={() => updateStatus(b.id, "confirmed")} className="rounded-full bg-[#2C4C3B]" data-testid={`approve-${b.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => updateStatus(b.id, "rejected")} className="rounded-full border-[#C86650] text-[#C86650]" data-testid={`reject-${b.id}`}>Reject</Button>
                      </>
                    )}
                    {!b.is_archived && b.status !== "rejected" && b.status !== "cancelled" && (
                      <>
                        <Button size="sm" variant="outline" data-testid={`edit-booking-${b.id}`} onClick={() => setEditingBooking(b)} className="rounded-full border-[#2C4C3B] text-[#2C4C3B]">Edit</Button>
                        <Button size="sm" variant="outline" data-testid={`cancel-booking-${b.id}`} onClick={() => updateStatus(b.id, "cancelled")} className="rounded-full border-[#5C6A64] text-[#5C6A64]">Cancel</Button>
                      </>
                    )}
                    {!b.deposit_paid && (
                      <MarkDepositPaidDialog booking={b} onDone={load} />
                    )}
                    {b.deposit_paid && !b.deposit_refunded && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`mark-deposit-unpaid-${b.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Reset the deposit for ${b.user_name || "this booking"} to unpaid? This removes any manual reconciliation tx from Payments.`)) return;
                          try {
                            await api.post(`/admin/bookings/${b.id}/deposit-status`, { paid: false });
                            toast.success("Deposit set to unpaid");
                            load();
                          } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                        }}
                        className="rounded-full border-[#5C6A64] text-[#5C6A64]"
                      >
                        Mark unpaid
                      </Button>
                    )}
                    {!b.deposit_paid && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`send-deposit-email-${b.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Email a Stripe deposit link to ${b.user_email}?`)) return;
                          try {
                            await api.post(`/admin/bookings/${b.id}/send-deposit-email`);
                            toast.success("Deposit email sent");
                            load();
                          } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
                        }}
                        className="rounded-full border-[#2C4C3B] text-[#2C4C3B]"
                      >
                        <MailWarning className="w-3 h-3 mr-1" /> Send deposit email
                      </Button>
                    )}
                    {b.deposit_paid && !b.deposit_refunded && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`refund-deposit-${b.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Refund $${(b.deposit_amount || 0).toFixed(2)} deposit to ${b.user_email}?`)) return;
                          try {
                            await api.post(`/bookings/${b.id}/refund-deposit`);
                            toast.success("Deposit refunded");
                            load();
                          } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
                        }}
                        className="rounded-full border-[#C86650] text-[#C86650]"
                      >
                        Refund deposit
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`delete-booking-${b.id}`}
                      onClick={async () => {
                        if (!window.confirm(`Delete this booking? ${b.deposit_paid && !b.deposit_refunded ? "Refund the deposit first — this will be blocked." : "This cannot be undone."}`)) return;
                        try {
                          await api.delete(`/bookings/${b.id}`);
                          toast.success("Booking deleted");
                          load();
                        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
                      }}
                      className="rounded-full border-[#C86650] text-[#C86650]"
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ));
            })()}
          </div>
          {editingBooking && (
            <EditBookingDialog booking={editingBooking} amenities={amenities} properties={properties} onClose={() => setEditingBooking(null)} onDone={load} />
          )}
        </TabsContent>

        <TabsContent value="announcements">
          <AnnouncementsTab items={announcements} onReload={load} />
        </TabsContent>

        <TabsContent value="amenities">
          <AmenitiesTab amenities={amenities} onReload={load} />
        </TabsContent>

        <TabsContent value="documents">
          <DocumentsTab />
        </TabsContent>

        <TabsContent value="board">
          <BoardTab users={users} properties={properties} onReload={load} />
        </TabsContent>

        <TabsContent value="volunteers">
          <VolunteersTab />
        </TabsContent>

        <TabsContent value="interests">
          <CommunityInterestsTab />
        </TabsContent>

        <TabsContent value="messages">
          <div className="flex items-center gap-2 mt-4 mb-3 flex-wrap">
            <Button size="sm" variant={msgFilter === "inbox" ? "default" : "outline"} onClick={() => setMsgFilter("inbox")} className={msgFilter === "inbox" ? "rounded-full bg-[#2C4C3B]" : "rounded-full border-[#D5CFC4]"} data-testid="msg-filter-inbox">Inbox ({messages.filter((m) => !m.archived).length})</Button>
            <Button size="sm" variant={msgFilter === "requests" ? "default" : "outline"} onClick={() => setMsgFilter("requests")} className={msgFilter === "requests" ? "rounded-full bg-[#C86650]" : "rounded-full border-[#C86650] text-[#C86650]"} data-testid="msg-filter-requests">
              Homeowner requests ({pendingRequests})
            </Button>
            <Button size="sm" variant={msgFilter === "archived" ? "default" : "outline"} onClick={() => setMsgFilter("archived")} className={msgFilter === "archived" ? "rounded-full bg-[#2C4C3B]" : "rounded-full border-[#D5CFC4]"} data-testid="msg-filter-archived">Archived ({messages.filter((m) => m.archived).length})</Button>
            <Button size="sm" variant={msgFilter === "all" ? "default" : "outline"} onClick={() => setMsgFilter("all")} className={msgFilter === "all" ? "rounded-full bg-[#2C4C3B]" : "rounded-full border-[#D5CFC4]"} data-testid="msg-filter-all">All ({messages.length})</Button>
          </div>
          {msgFilter === "requests" && (
            <div className="mb-4 p-3 rounded-2xl bg-[#EADCC8] border border-[#D5CFC4] text-sm text-[#1A2421]" data-testid="requests-hint">
              <b>Homeowner requests queue:</b> proposed payment plans, "call me" requests, and balance-dispute submissions. Review each one, jump to the property ledger or set up an arrangement, then archive to clear from this list.
            </div>
          )}
          <div className="space-y-3">
            {visibleMessages.length === 0 && <p className="text-[#5C6A64]" data-testid="admin-messages-empty">{msgFilter === "requests" ? "No pending homeowner requests. 🎉" : "No messages."}</p>}
            {visibleMessages.map((m) => (
              <Card key={m.id} data-testid={`admin-message-${m.id}`} className={`rounded-2xl border-[#D5CFC4] ${m.archived ? "opacity-60" : ""} ${!m.archived && (m.kind === "propose_plan" || m.kind === "contact_me" || m.kind === "balance_dispute" || m.has_new_homeowner_reply) ? "border-l-4 border-l-[#C86650]" : ""}`}>
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-2 gap-3">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className="font-medium text-[#1A2421]">{m.subject}</p>
                        {m.kind === "propose_plan" && <Badge className="bg-[#C86650]" data-testid={`msg-kind-${m.id}`}>Proposed plan</Badge>}
                        {m.kind === "contact_me" && <Badge className="bg-[#C86650]" data-testid={`msg-kind-${m.id}`}>Contact request</Badge>}
                        {m.kind === "balance_dispute" && <Badge className="bg-[#C86650]" data-testid={`msg-kind-${m.id}`}>Balance dispute</Badge>}
                        {!m.read && <Badge className="bg-[#2C4C3B]">New</Badge>}
                        {m.has_new_homeowner_reply && <Badge className="bg-[#C86650] animate-pulse" data-testid={`msg-has-reply-${m.id}`}>New homeowner reply</Badge>}
                        {m.archived && <Badge variant="outline" className="border-[#5C6A64] text-[#5C6A64]">Archived</Badge>}
                      </div>
                      <p className="text-sm text-[#5C6A64]">{m.name} ({m.email}) {m.phone && `· ${m.phone}`}{(m.arrangement_data?.preferred_contact || m.dispute_data?.preferred_contact) && ` · prefers ${m.arrangement_data?.preferred_contact || m.dispute_data?.preferred_contact}`}</p>
                    </div>
                    <span className="text-xs text-[#5C6A64] whitespace-nowrap">{new Date(m.created_at).toLocaleDateString()}</span>
                  </div>
                  {m.arrangement_data && (m.arrangement_data.deposit_amount != null || m.arrangement_data.monthly_amount != null || m.arrangement_data.first_due_date) && (
                    <div className="mb-3 p-3 rounded-xl bg-[#FDF9F1] border border-[#EADCC8] text-sm" data-testid={`msg-proposal-${m.id}`}>
                      <p className="text-xs uppercase tracking-wider text-[#C86650] mb-1">Homeowner's proposal</p>
                      {m.arrangement_data.deposit_amount != null && <p>Deposit: <b>${Number(m.arrangement_data.deposit_amount).toFixed(2)}</b></p>}
                      {m.arrangement_data.monthly_amount != null && <p>Monthly: <b>${Number(m.arrangement_data.monthly_amount).toFixed(2)}</b></p>}
                      {m.arrangement_data.first_due_date && <p>Proposed first due date: <b>{m.arrangement_data.first_due_date}</b></p>}
                      {m.arrangement_data.address && <p className="text-xs text-[#5C6A64] mt-1">Property: {m.arrangement_data.address}{m.arrangement_data.unit_number ? ` · Unit ${m.arrangement_data.unit_number}` : ""}</p>}
                    </div>
                  )}
                  {m.dispute_data && (() => {
                    const disputedBal = Number(m.dispute_data.balance || 0);
                    const currentProp = properties.find((p) => p.id === m.dispute_data.property_id);
                    const currentBal = currentProp ? Number(currentProp.balance || 0) : null;
                    const delta = currentBal !== null ? (disputedBal - currentBal) : null;
                    return (
                    <div className="mb-3 p-3 rounded-xl bg-[#FDF9F1] border border-[#EADCC8] text-sm" data-testid={`msg-dispute-${m.id}`}>
                      <p className="text-xs uppercase tracking-wider text-[#C86650] mb-1">Homeowner's ledger snapshot</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                        <p className="text-[#5C6A64]">Reason:</p><p><b>{m.dispute_data.reason_label || m.dispute_data.reason}</b></p>
                        <p className="text-[#5C6A64]">Property:</p><p>{m.dispute_data.address} · Unit {m.dispute_data.unit_number}</p>
                        <p className="text-[#5C6A64]">Balance shown:</p><p className="text-[#C86650]">${disputedBal.toFixed(2)}</p>
                        {currentBal !== null && (<>
                          <p className="text-[#5C6A64]">Current balance:</p>
                          <p className={currentBal > 0 ? "text-[#C86650]" : "text-[#2C4C3B]"}>${currentBal.toFixed(2)}</p>
                        </>)}
                        <p className="text-[#5C6A64]">YTD paid:</p><p className="text-[#2C4C3B]">${Number(m.dispute_data.ytd_paid || 0).toFixed(2)}</p>
                        <p className="text-[#5C6A64]">Starting balance:</p><p>${Number(m.dispute_data.starting_balance || 0).toFixed(2)}</p>
                        <p className="text-[#5C6A64]">Annual assessment:</p><p>${Number(m.dispute_data.annual_assessment || 0).toFixed(2)}</p>
                        {m.dispute_data.last_payment_amount != null && (<>
                          <p className="text-[#5C6A64]">Last payment on record:</p>
                          <p>${Number(m.dispute_data.last_payment_amount).toFixed(2)} on {(m.dispute_data.last_payment_date || "").slice(0, 10)}</p>
                        </>)}
                      </div>
                      {delta !== null && Math.abs(delta) >= 0.01 && (
                        <div
                          data-testid={`msg-dispute-delta-${m.id}`}
                          className={`mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
                            delta > 0
                              ? "bg-[#EADCC8] text-[#2C4C3B] border border-[#2C4C3B]"
                              : "bg-[#FDF3F0] text-[#C86650] border border-[#C86650]"
                          }`}
                        >
                          {delta > 0
                            ? <>Balance dropped <b>${delta.toFixed(2)}</b> since — was ${disputedBal.toFixed(2)}, now ${currentBal.toFixed(2)} · likely stale</>
                            : <>Balance grew <b>${Math.abs(delta).toFixed(2)}</b> since — was ${disputedBal.toFixed(2)}, now ${currentBal.toFixed(2)}</>
                          }
                        </div>
                      )}
                      {delta !== null && Math.abs(delta) < 0.01 && (
                        <div data-testid={`msg-dispute-delta-${m.id}`} className="mt-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium bg-[#FDF3F0] text-[#C86650] border border-[#C86650]">
                          Unchanged since filed — homeowner's concern is still current
                        </div>
                      )}
                      {m.dispute_data.property_id && (
                        <div className="mt-2">
                          <Link
                            to={`/admin/properties/${m.dispute_data.property_id}`}
                            data-testid={`msg-open-ledger-${m.id}`}
                            className="text-xs text-[#C86650] underline"
                          >
                            Open property ledger →
                          </Link>
                        </div>
                      )}
                    </div>
                    );
                  })()}
                  <p className="text-[#1A2421] text-sm whitespace-pre-line mb-3">{m.message}</p>
                  {Array.isArray(m.replies) && m.replies.length > 0 && (
                    <div className="mb-3 space-y-2" data-testid={`msg-replies-${m.id}`}>
                      {m.replies.map((r, idx) => (
                        <div key={idx} className={`p-3 rounded-xl border-l-4 ${r.from_homeowner ? "bg-[#FDF3F0] border-l-[#C86650]" : "bg-[#F2EFE8] border-l-[#2C4C3B]"}`}>
                          <p className="text-xs text-[#5C6A64] mb-1">
                            {r.from_homeowner ? (
                              <><b className="text-[#C86650]">Homeowner replied</b> · {new Date(r.sent_at).toLocaleString()}</>
                            ) : (
                              <><b className="text-[#2C4C3B]">You</b>{r.reply_to ? ` · replies go to ${r.reply_to}` : ""} · {new Date(r.sent_at).toLocaleString()}</>
                            )}
                          </p>
                          <p className="text-sm text-[#1A2421] whitespace-pre-wrap">{r.body}</p>
                          {Array.isArray(r.attachments) && r.attachments.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2" data-testid={`reply-attachments-${m.id}-${idx}`}>
                              {r.attachments.map((a, ai) => (
                                <a
                                  key={ai}
                                  href={`${BACKEND}${a.url}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  data-testid={`reply-attachment-${m.id}-${idx}-${ai}`}
                                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#C86650] text-[#C86650] px-3 py-1 text-xs hover:bg-[#FDF3F0]"
                                >
                                  <Paperclip className="w-3 h-3" />
                                  <span className="max-w-[14rem] truncate">{a.filename}</span>
                                  <span className="text-[#5C6A64]">· {((a.size || 0) / 1024).toFixed(0)} KB</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-[#EADCC8]">
                    {!m.read && (
                      <Button size="sm" variant="outline" data-testid={`msg-read-${m.id}`} onClick={() => patchMessage(m.id, { read: true })} className="rounded-full border-[#2C4C3B] text-[#2C4C3B]">Mark read</Button>
                    )}
                    {m.has_new_homeowner_reply && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`msg-ack-reply-${m.id}`}
                        onClick={async () => {
                          try { await api.post(`/admin/messages/${m.id}/ack-homeowner-reply`); load(); }
                          catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                        }}
                        className="rounded-full border-[#C86650] text-[#C86650]"
                      >
                        Acknowledge reply
                      </Button>
                    )}
                    <GmailReplyDialog message={m} onDone={load} />
                    {(m.kind === "propose_plan" || m.kind === "contact_me") && !m.archived && (
                      <Button size="sm" variant="outline" data-testid={`msg-go-arrangements-${m.id}`} onClick={() => setAdminTab("arrangements")} className="rounded-full border-[#C86650] text-[#C86650]">
                        Set up arrangement →
                      </Button>
                    )}
                    {m.kind === "propose_plan" && !m.archived && (() => {
                      const ad = m.arrangement_data || {};
                      let matched = ad.property_id ? properties.find((p) => p.id === ad.property_id) : null;
                      if (!matched && ad.address) {
                        matched = properties.find((p) => p.address === ad.address && (!ad.unit_number || String(p.unit_number) === String(ad.unit_number)));
                      }
                      if (!matched) return null;
                      const isSettlement = ad.settlement_kind === "option2_50_payment_plan";
                      // For 50% settlement proposals, build a full installment schedule and pre-select Option 2.
                      let customRows;
                      let totalAmount;
                      let noteLine;
                      if (isSettlement && ad.installments && ad.monthly_amount != null && ad.first_due_date) {
                        const [y, mo, d] = ad.first_due_date.split("-").map(Number);
                        customRows = [];
                        for (let i = 0; i < ad.installments; i++) {
                          const dt = new Date(y, (mo - 1) + i, d);
                          customRows.push({
                            amount: Number(ad.monthly_amount),
                            due_date: `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`,
                          });
                        }
                        totalAmount = Number(matched.balance) * 0.5;
                        noteLine = `Accepted 50% settlement proposal (msg ${m.id}) — ${ad.installments} monthly installments of $${Number(ad.monthly_amount).toFixed(2)} starting ${ad.first_due_date}. Applied Option 2 (50% discount).`;
                      } else {
                        customRows = (ad.monthly_amount != null && ad.first_due_date)
                          ? [{ amount: Number(ad.monthly_amount), due_date: ad.first_due_date }]
                          : undefined;
                        totalAmount = matched.balance;
                        noteLine = `Accepted homeowner proposal (msg ${m.id}) — deposit $${Number(ad.deposit_amount || 0).toFixed(2)} + $${Number(ad.monthly_amount || 0).toFixed(2)}/mo starting ${ad.first_due_date || ""}`;
                      }
                      const preset = {
                        mode: "custom",
                        initial_deposit: ad.deposit_amount,
                        custom_rows: customRows,
                        monthly_amount: ad.monthly_amount,
                        first_due_date: ad.first_due_date,
                        total_amount: totalAmount,
                        notes: noteLine,
                        apply_settlement: isSettlement ? "option2_50_payment_plan" : null,
                      };
                      return (
                        <Button
                          size="sm"
                          data-testid={`msg-accept-proposal-${m.id}`}
                          onClick={async () => {
                            setAcceptProposal({ property: matched, preset, messageId: m.id });
                            if (!m.read) {
                              try { await patchMessage(m.id, { read: true }); } catch { /* non-blocking */ }
                            }
                          }}
                          className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3D2E]"
                        >
                          {isSettlement ? "Accept 50% settlement →" : "Accept proposal → arrangement"}
                        </Button>
                      );
                    })()}
                    {m.kind === "propose_plan" && !m.archived && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`msg-decline-proposal-${m.id}`}
                        onClick={async () => {
                          const reason = window.prompt(
                            "Optional note to the homeowner explaining why the plan can't be approved (leave blank to send the default templated email):",
                            ""
                          );
                          if (reason === null) return; // cancelled
                          try {
                            await api.post(`/contact/messages/${m.id}/decline-plan`, { reason_note: reason || null });
                            toast.success("Homeowner emailed · message archived");
                            load();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Failed to send decline");
                          }
                        }}
                        className="rounded-full border-[#C86650] text-[#C86650] hover:bg-[#C86650] hover:text-white"
                      >
                        Decline plan → email
                      </Button>
                    )}
                    {m.archived ? (
                      <Button size="sm" variant="outline" data-testid={`msg-unarchive-${m.id}`} onClick={() => patchMessage(m.id, { archived: false })} className="rounded-full border-[#5C6A64] text-[#5C6A64]">Unarchive</Button>
                    ) : (
                      <Button size="sm" variant="outline" data-testid={`msg-archive-${m.id}`} onClick={() => patchMessage(m.id, { archived: true })} className="rounded-full border-[#5C6A64] text-[#5C6A64]">Archive</Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`msg-delete-${m.id}`} onClick={() => deleteMessage(m.id)} className="rounded-full border-[#C86650] text-[#C86650]"><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      {acceptProposal && (
        <ArrangementDialog
          key={`accept-${acceptProposal.messageId}`}
          property={acceptProposal.property}
          defaultAmount={acceptProposal.property?.balance}
          preset={acceptProposal.preset}
          open={true}
          onOpenChange={(v) => { if (!v) setAcceptProposal(null); }}
          hideTrigger
          onDone={() => { setAcceptProposal(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Properties Tab ----------
function PropertiesTab({ properties, onReload }) {
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [assigningProp, setAssigningProp] = useState(null); // property to which we're assigning a member
  const [sortKey, setSortKey] = useState("address");
  const [filter, setFilter] = useState("all"); // all, current, delinquent, partial
  const [search, setSearch] = useState("");

  // Memoized visible-properties computation — avoids re-filtering the entire list
  // on every unrelated re-render of PropertiesTab (search input, dialog open, etc.).
  const visibleProperties = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return (properties || []).filter((p) => {
      if (filter !== "all" && p.status !== filter) return false;
      if (!q) return true;
      return (
        (p.address || "").toLowerCase().includes(q) ||
        (p.unit_number || "").toString().toLowerCase().includes(q) ||
        (p.owner_name || "").toLowerCase().includes(q) ||
        (p.owner_email || "").toLowerCase().includes(q)
      );
    });
  }, [properties, filter, search]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try {
      await api.delete(`/admin/properties/${id}`);
      toast.success("Deleted");
      onReload();
    } catch { toast.error("Failed"); }
  };

  const q = search.trim().toLowerCase();
  const filtered = properties.filter((p) => {
    if (filter !== "all" && p.status !== filter) return false;
    if (!q) return true;
    return (
      (p.address || "").toLowerCase().includes(q) ||
      (p.unit_number || "").toString().toLowerCase().includes(q) ||
      (p.owner_name || "").toLowerCase().includes(q) ||
      (p.owner_email || "").toLowerCase().includes(q)
    );
  });
  // Category counts across ALL properties (search-filtered but ignoring status filter),
  // so admins always see how many delinquent / current / partial exist regardless of the active filter.
  const searchScoped = q
    ? properties.filter((p) => (
        (p.address || "").toLowerCase().includes(q) ||
        (p.unit_number || "").toString().toLowerCase().includes(q) ||
        (p.owner_name || "").toLowerCase().includes(q) ||
        (p.owner_email || "").toLowerCase().includes(q)
      ))
    : properties;
  const counts = {
    total: searchScoped.length,
    current: searchScoped.filter((p) => p.status === "current").length,
    partial: searchScoped.filter((p) => p.status === "partial").length,
    delinquent: searchScoped.filter((p) => p.status === "delinquent").length,
  };
  const sorted = [...filtered].sort((a, b) => {
    if (sortKey === "balance") return (b.balance || 0) - (a.balance || 0);
    if (sortKey === "ytd_paid") return (b.ytd_paid || 0) - (a.ytd_paid || 0);
    if (sortKey === "owner_name") return (a.owner_name || "").localeCompare(b.owner_name || "");
    if (sortKey === "last_payment") {
      const da = a.last_payment_date || ""; const db = b.last_payment_date || "";
      return db.localeCompare(da);
    }
    return (a.address || "").localeCompare(b.address || "");
  });

  return (
    <>
      <div className="flex justify-between items-center mt-4 mb-4 gap-3 flex-wrap">
        <div className="flex gap-2 items-center flex-wrap">
          <Input
            data-testid="property-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search address, unit, owner, email…"
            className="w-72 rounded-full bg-white h-9"
          />
          <p className="text-sm text-[#5C6A64]" data-testid="property-counts">
            {filter === "all" ? (
              <><b className="text-[#1A2421]">{counts.total}</b> total · <span className="text-[#C86650]">{counts.delinquent} delinquent</span> · <span className="text-[#EADCC8] bg-[#5C6A64] px-1.5 rounded">{counts.partial} partial</span> · <span className="text-[#2C4C3B]">{counts.current} current</span></>
            ) : (
              <><b className="text-[#1A2421]">{filtered.length}</b> {filter} of <b>{counts.total}</b> total <span className="text-xs">({counts.delinquent} delinquent · {counts.partial} partial · {counts.current} current)</span></>
            )}
          </p>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="property-filter-select" className="w-40 rounded-full bg-white h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All properties</SelectItem>
              <SelectItem value="current">Current (no balance)</SelectItem>
              <SelectItem value="partial">Partial payment</SelectItem>
              <SelectItem value="delinquent">Delinquent</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortKey} onValueChange={setSortKey}>
            <SelectTrigger data-testid="property-sort-select" className="w-48 rounded-full bg-white h-9"><ArrowUpDown className="w-3 h-3 mr-2" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="address">Sort by address</SelectItem>
              <SelectItem value="owner_name">Sort by owner name</SelectItem>
              <SelectItem value="balance">Sort by balance (high→low)</SelectItem>
              <SelectItem value="ytd_paid">Sort by YTD paid</SelectItem>
              <SelectItem value="last_payment">Sort by last payment</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={`${BACKEND}/api/admin/properties/export`} data-testid="download-properties-btn" target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B]"><Download className="w-4 h-4 mr-2" /> Download CSV</Button>
          </a>
          <Button data-testid="bulk-assessment-btn" variant="outline" onClick={() => setBulkOpen(true)} className="rounded-full border-[#D5CFC4]">
            <RefreshCw className="w-4 h-4 mr-2" /> Bulk Update Dues
          </Button>
          <a href={`${BACKEND}/api/admin/properties/template`} data-testid="download-template-btn" target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-full border-[#D5CFC4]"><Download className="w-4 h-4 mr-2" /> Template</Button>
          </a>
          <Button data-testid="import-csv-btn" variant="outline" onClick={() => setImportOpen(true)} className="rounded-full border-[#2C4C3B] text-[#2C4C3B]"><Upload className="w-4 h-4 mr-2" /> Import CSV</Button>
          <Button data-testid="add-property-btn" onClick={() => setAddOpen(true)} className="rounded-full bg-[#2C4C3B]"><Plus className="w-4 h-4 mr-2" /> Add Property</Button>
        </div>
      </div>

      <Card className="rounded-2xl border-[#D5CFC4]">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#EADCC8] text-[#2C4C3B]">
              <tr>
                <th className="text-left px-4 py-3">Address</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-right px-4 py-3">Annual</th>
                <th className="text-right px-4 py-3">YTD Paid</th>
                <th className="text-right px-4 py-3">Balance</th>
                <th className="text-left px-4 py-3">Last Payment</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p) => (
                <tr key={p.id} data-testid={`property-row-${p.id}`} className="border-t border-[#D5CFC4] hover:bg-[#F9F8F6]">
                  <td className="px-4 py-3">
                    <Link to={`/admin/properties/${p.id}`} className="font-medium text-[#1A2421] hover:text-[#2C4C3B] hover:underline" data-testid={`prop-link-${p.id}`}>{p.address}</Link>
                    <p className="text-xs text-[#5C6A64]">Unit {p.unit_number}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p>{p.owner_name}</p>
                    {p.owner_email && <p className="text-xs text-[#5C6A64]">{p.owner_email}</p>}
                  </td>
                  <td className="px-4 py-3 text-right">${p.annual_assessment.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-[#2C4C3B]">${p.ytd_paid.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-right font-medium ${p.balance > 0 ? "text-[#C86650]" : "text-[#2C4C3B]"}`}>${p.balance.toFixed(2)}</td>
                  <td className="px-4 py-3 text-xs text-[#5C6A64]">
                    {p.last_payment_date ? `${new Date(p.last_payment_date).toLocaleDateString()} — $${p.last_payment_amount?.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={p.status === "current" ? "bg-[#2C4C3B]" : p.status === "partial" ? "bg-[#EADCC8] text-[#2C4C3B]" : "bg-[#C86650]"}>
                      {p.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      {!p.linked_user_id && !p.owner_user_id && (
                        <Button size="sm" variant="outline" data-testid={`assign-member-inline-${p.id}`} onClick={() => setAssigningProp(p)} className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white h-8 px-3 text-xs">
                          <UserPlus className="w-3 h-3 mr-1" /> Assign
                        </Button>
                      )}
                      <ViewPaymentsButton propertyId={p.id} unit={p.unit_number} />
                      <Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-property-${p.id}`}><Edit className="w-4 h-4 text-[#5C6A64]" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => handleDelete(p.id)} data-testid={`delete-property-${p.id}`}><Trash2 className="w-4 h-4 text-[#C86650]" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-[#5C6A64]">No properties match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <PropertyDialog open={addOpen} onOpenChange={setAddOpen} onDone={onReload} />
      <PropertyDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} initial={editing} onDone={onReload} />
      <ImportCsvDialog open={importOpen} onOpenChange={setImportOpen} onDone={onReload} />
      <BulkAssessmentDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={onReload} />
      <AssignMemberToPropertyDialog open={!!assigningProp} onOpenChange={(o) => !o && setAssigningProp(null)} property={assigningProp} onDone={onReload} />
    </>
  );
}


function BulkAssessmentDialog({ open, onOpenChange, onDone }) {
  const [form, setForm] = useState({ new_amount: 350, filter_amount: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const body = { new_amount: parseFloat(form.new_amount) };
      if (form.filter_amount) body.filter_amount = parseFloat(form.filter_amount);
      const { data } = await api.post("/admin/properties/bulk-assessment", body);
      toast.success(`Updated ${data.updated} properties to $${data.new_amount.toFixed(2)}`);
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Bulk Update Annual Dues</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#5C6A64]">Update annual assessment amounts across multiple properties at once.</p>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">New annual amount ($)</label>
            <Input data-testid="bulk-new-amount" type="number" step="0.01" value={form.new_amount} onChange={(e) => setForm({ ...form, new_amount: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Only update properties currently at (optional)</label>
            <Input data-testid="bulk-filter-amount" type="number" step="0.01" placeholder="Leave blank to update ALL" value={form.filter_amount} onChange={(e) => setForm({ ...form, filter_amount: e.target.value })} className="mt-1 rounded-xl" />
            <p className="text-xs text-[#5C6A64] mt-1">Leave blank to update every property. Enter a value (e.g. 300) to only change properties currently at that amount.</p>
          </div>
          <Button data-testid="bulk-save-btn" onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2C4C3B]">
            {busy ? "Updating…" : "Apply update"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Announcements Tab ----------
const ANN_CATEGORIES = ["General", "Community", "Maintenance", "Board", "Event"];

function AnnouncementsTab({ items, onReload }) {
  const [showPending, setShowPending] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", category: "General", is_public: true, event_date: "" });

  const pending = items.filter((a) => a.status === "pending");
  const others = items.filter((a) => a.status !== "pending");
  const visible = showPending ? pending : others;

  const publish = async () => {
    if (!form.title || !form.body) return toast.error("Title and description required");
    try {
      await api.post("/announcements", form);
      toast.success("Announcement published");
      setOpenCreate(false);
      setForm({ title: "", body: "", category: "General", is_public: true, event_date: "" });
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const approve = async (id) => {
    try { await api.post(`/admin/announcements/${id}/approve`); toast.success("Approved"); onReload(); }
    catch { toast.error("Failed"); }
  };
  const reject = async (id) => {
    if (!window.confirm("Reject this announcement?")) return;
    try { await api.post(`/admin/announcements/${id}/reject`); toast.success("Rejected"); onReload(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete permanently?")) return;
    try { await api.delete(`/announcements/${id}`); toast.success("Deleted"); onReload(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex gap-2">
          <Button
            data-testid="ann-toggle-pending"
            variant={showPending ? "default" : "outline"}
            onClick={() => setShowPending(true)}
            className={`rounded-full ${showPending ? "bg-[#C86650] hover:bg-[#B4553F]" : "border-[#C86650] text-[#C86650]"}`}
          >
            <Clock className="w-4 h-4 mr-2" /> Pending {pending.length > 0 && <Badge className="ml-2 bg-white text-[#C86650]">{pending.length}</Badge>}
          </Button>
          <Button
            data-testid="ann-toggle-published"
            variant={!showPending ? "default" : "outline"}
            onClick={() => setShowPending(false)}
            className={`rounded-full ${!showPending ? "bg-[#2C4C3B]" : "border-[#2C4C3B] text-[#2C4C3B]"}`}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" /> Published ({others.length})
          </Button>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button data-testid="admin-new-ann-btn" className="rounded-full bg-[#2C4C3B]">
              <Plus className="w-4 h-4 mr-2" /> New Announcement
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Publish Announcement</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <Input data-testid="admin-ann-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <Textarea data-testid="admin-ann-body" placeholder="Description" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="admin-ann-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{ANN_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="admin-ann-event-date" type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} placeholder="Event date (optional)" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="admin-ann-public" type="checkbox" checked={form.is_public} onChange={(e) => setForm({ ...form, is_public: e.target.checked })} />
                Public (visible to non-members)
              </label>
              <Button data-testid="admin-ann-publish" onClick={publish} className="w-full rounded-full bg-[#2C4C3B]">Publish</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {showPending && pending.length > 0 && (
        <div className="p-4 bg-[#FDF3F0] border border-[#C86650] rounded-2xl">
          <p className="text-sm text-[#C86650] font-medium">Board Review Queue — {pending.length} pending submission{pending.length === 1 ? "" : "s"}</p>
        </div>
      )}

      {visible.length === 0 ? (
        <Card className="rounded-2xl border-[#D5CFC4]"><CardContent className="p-12 text-center text-[#5C6A64]">
          {showPending ? "No pending submissions." : "No published announcements yet."}
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {visible.map((a) => (
            <Card key={a.id} data-testid={`admin-ann-${a.id}`} className="rounded-2xl border-[#D5CFC4]">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-serif text-xl text-[#1A2421]">{a.title}</p>
                      {a.pinned && <Badge data-testid={`admin-ann-pinned-${a.id}`} className="bg-[#C86650] text-white"><Pin className="w-3 h-3 mr-1" />Pinned</Badge>}
                      <Badge className="bg-[#2C4C3B]">{a.category}</Badge>
                      {a.status === "pending" && <Badge className="bg-[#C86650]">Pending</Badge>}
                      {a.status === "rejected" && <Badge className="bg-[#5C6A64]">Rejected</Badge>}
                      {a.event_date && <Badge variant="outline" className="border-[#2C4C3B]">📅 {formatLocalDate(a.event_date)}</Badge>}
                    </div>
                    <p className="text-xs text-[#5C6A64] mb-2">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString() : ""} · {a.author || a.contact_name}
                      {a.contact_email && ` (${a.contact_email})`}
                    </p>
                    <p className="text-sm text-[#1A2421] whitespace-pre-line">{a.body}</p>
                  </div>
                  <div className="flex gap-1 flex-wrap">
                    {a.status === "pending" && (
                      <>
                        <Button size="sm" onClick={() => approve(a.id)} data-testid={`admin-approve-ann-${a.id}`} className="rounded-full bg-[#2C4C3B]">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => reject(a.id)} data-testid={`admin-reject-ann-${a.id}`} className="rounded-full border-[#C86650] text-[#C86650]">
                          <XCircle className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    <Button size="icon" variant="ghost" data-testid={`admin-edit-ann-${a.id}`} title="Edit announcement" onClick={() => setEditing(a)}>
                      <Pencil className="w-4 h-4 text-[#2C4C3B]" />
                    </Button>
                    {a.status !== "pending" && a.status !== "rejected" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`admin-pin-ann-${a.id}`}
                        title={a.pinned ? "Unpin" : "Pin to top"}
                        onClick={async () => {
                          try { await api.post(`/admin/announcements/${a.id}/pin`); toast.success(a.pinned ? "Unpinned" : "Pinned to top"); onReload(); }
                          catch { toast.error("Failed"); }
                        }}
                      >
                        <Pin className={`w-4 h-4 ${a.pinned ? "text-[#C86650] fill-[#C86650]" : "text-[#5C6A64]"}`} />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" data-testid={`admin-delete-ann-${a.id}`} onClick={() => remove(a.id)}>
                      <Trash2 className="w-4 h-4 text-[#C86650]" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Edit announcement</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input data-testid="edit-ann-title" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Title" />
              <Textarea data-testid="edit-ann-body" rows={5} value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })} placeholder="Description" />
              <div className="grid grid-cols-2 gap-3">
                <Select value={editing.category || "General"} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="edit-ann-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{ANN_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="edit-ann-event-date" type="date" value={editing.event_date || ""} onChange={(e) => setEditing({ ...editing, event_date: e.target.value })} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="edit-ann-public" type="checkbox" checked={editing.is_public !== false} onChange={(e) => setEditing({ ...editing, is_public: e.target.checked })} />
                Public (visible to non-members)
              </label>
              <div className="flex gap-2">
                <Button
                  data-testid="edit-ann-save"
                  className="flex-1 rounded-full bg-[#2C4C3B]"
                  onClick={async () => {
                    try {
                      await api.patch(`/admin/announcements/${editing.id}`, {
                        title: editing.title, body: editing.body, category: editing.category,
                        is_public: editing.is_public, event_date: editing.event_date || null,
                      });
                      toast.success("Announcement updated");
                      setEditing(null);
                      onReload();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}
                >
                  <Save className="w-4 h-4 mr-2" /> Save changes
                </Button>
                {editing.status === "pending" && (
                  <Button
                    data-testid="edit-ann-approve"
                    className="rounded-full bg-[#C86650]"
                    onClick={async () => {
                      try {
                        await api.patch(`/admin/announcements/${editing.id}`, {
                          title: editing.title, body: editing.body, category: editing.category,
                          is_public: editing.is_public, event_date: editing.event_date || null,
                        });
                        await api.post(`/admin/announcements/${editing.id}/approve`);
                        toast.success("Updated & approved");
                        setEditing(null);
                        onReload();
                      } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                    }}
                  >
                    Save & approve
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ---------- Payment Arrangements ----------
function ArrangementDialog({ property, defaultAmount, onDone, preset, open: controlledOpen, onOpenChange: controlledOnOpenChange, hideTrigger = false }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    if (isControlled) controlledOnOpenChange?.(v);
    else setInternalOpen(v);
  };
  const [mode, setMode] = useState("uniform"); // "uniform" | "custom"
  const [form, setForm] = useState({
    total_amount: defaultAmount ? Number(defaultAmount).toFixed(2) : "",
    installments: 3,
    monthly_amount: "",
    first_due_date: new Date(Date.now() + 7 * 86400 * 1000).toISOString().slice(0, 10),
    frequency: "monthly",
    notes: "",
    initial_deposit: "",
    deposit_method: "check",
    deposit_reference: "",
  });
  const [customRows, setCustomRows] = useState([
    { amount: "", due_date: new Date(Date.now() + 30 * 86400 * 1000).toISOString().slice(0, 10) },
  ]);
  const [busy, setBusy] = useState(false);
  const [settlementOption, setSettlementOption] = useState("none"); // "none" | "option1_65_paid_in_full" | "option2_50_payment_plan"
  const [deadlineHint, setDeadlineHint] = useState("");

  const balanceNum = Number(property?.balance || 0);
  const existingSettlement = property?.active_settlement || null;

  // Custom settlement state — admin can enter EITHER a discount % or an amount-due $;
  // whichever they type, the other stays in sync (percent = 100·(1 − amount/balance)).
  const [customPct, setCustomPct] = useState("40");
  const [customAmount, setCustomAmount] = useState(() =>
    balanceNum > 0 ? (balanceNum * 0.60).toFixed(2) : "0.00"
  );

  // Discount + amount_due preview based on current selection
  const settlementPreview = (() => {
    if (settlementOption === "option1_65_paid_in_full") {
      const discount = Math.round(balanceNum * 0.65 * 100) / 100;
      return { discount, amount_due: Math.round((balanceNum - discount) * 100) / 100, pct: 65 };
    }
    if (settlementOption === "option2_50_payment_plan") {
      const discount = Math.round(balanceNum * 0.50 * 100) / 100;
      return { discount, amount_due: Math.round((balanceNum - discount) * 100) / 100, pct: 50 };
    }
    if (settlementOption === "option3_35_paid_in_full") {
      const discount = Math.round(balanceNum * 0.35 * 100) / 100;
      return { discount, amount_due: Math.round((balanceNum - discount) * 100) / 100, pct: 35 };
    }
    if (settlementOption === "option_custom_paid_in_full") {
      const pctNum = Math.max(1, Math.min(99, parseFloat(customPct) || 0));
      const discount = Math.round(balanceNum * (pctNum / 100) * 100) / 100;
      return { discount, amount_due: Math.round((balanceNum - discount) * 100) / 100, pct: pctNum, custom: true };
    }
    return null;
  })();

  // When Option 2 is selected, auto-sync total_amount to the discounted amount_due so uniform-mode math is correct.
  // Custom Schedule mode is also allowed for Option 2 — but the rows must sum exactly to amount_due − deposit.
  useEffect(() => {
    if (settlementOption === "option2_50_payment_plan" && settlementPreview) {
      setForm((prev) => ({ ...prev, total_amount: settlementPreview.amount_due.toFixed(2) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlementOption, balanceNum]);

  // Fetch the global settlement deadline hint when the dialog opens (for UX only)
  useEffect(() => {
    if (!open || deadlineHint) return;
    api.get("/admin/settings/settlement").then(({ data }) => {
      if (data?.settlement_deadline) setDeadlineHint(data.settlement_deadline);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When opened with a preset (e.g. accepting a homeowner's proposal), pre-fill fields once per open.
  useEffect(() => {
    if (!open) return;
    // Reset settlement selection on every open — prevents a stale Option 2 leaking between properties.
    setSettlementOption("none");
    if (!preset) {
      // Reset total_amount to defaultAmount so a previous Option-2 auto-populate doesn't linger.
      setForm((prev) => ({ ...prev, total_amount: defaultAmount ? Number(defaultAmount).toFixed(2) : "" }));
      return;
    }
    if (preset.mode === "custom" && Array.isArray(preset.custom_rows) && preset.custom_rows.length > 0) {
      setMode("custom");
      setCustomRows(preset.custom_rows.map((r) => ({ amount: String(r.amount ?? ""), due_date: r.due_date || "" })));
    } else {
      setMode("uniform");
    }
    if (preset.apply_settlement) {
      setSettlementOption(preset.apply_settlement);
    }
    setForm((prev) => ({
      ...prev,
      total_amount: preset.total_amount != null ? Number(preset.total_amount).toFixed(2) : (defaultAmount ? Number(defaultAmount).toFixed(2) : prev.total_amount),
      monthly_amount: preset.monthly_amount != null ? String(preset.monthly_amount) : prev.monthly_amount,
      first_due_date: preset.first_due_date || prev.first_due_date,
      initial_deposit: preset.initial_deposit != null ? String(preset.initial_deposit) : prev.initial_deposit,
      notes: preset.notes != null ? preset.notes : prev.notes,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addRow = () => {
    const last = customRows[customRows.length - 1];
    const nextDate = last?.due_date ? new Date(last.due_date + "T12:00:00") : new Date();
    nextDate.setMonth(nextDate.getMonth() + 1);
    setCustomRows([...customRows, { amount: "", due_date: nextDate.toISOString().slice(0, 10) }]);
  };
  const removeRow = (i) => setCustomRows(customRows.filter((_, idx) => idx !== i));
  const updateRow = (i, field, val) => setCustomRows(customRows.map((r, idx) => idx === i ? { ...r, [field]: val } : r));

  const customTotal = customRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const customValid = customRows.length > 0 && customRows.every((r) => parseFloat(r.amount) > 0 && r.due_date);

  const submit = async () => {
    const deposit = parseFloat(form.initial_deposit) || 0;
    if (deposit < 0) return toast.error("Deposit cannot be negative");
    setBusy(true);
    try {
      // Branch: applying a settlement offer? → hit the settlement/apply endpoint (also creates the arrangement for Option 2)
      if (settlementOption !== "none") {
        if (existingSettlement) throw new Error("A settlement is already active on this property. Cancel it first.");
        if (balanceNum <= 0) throw new Error("Property has no past-due balance to settle");
        const settleBody = {
          option: settlementOption,
          notes: form.notes,
        };
        if (settlementOption === "option2_50_payment_plan") {
          settleBody.deposit_amount = deposit;
          settleBody.deposit_method = form.deposit_method;
          settleBody.deposit_reference = form.deposit_reference;
          if (mode === "custom") {
            if (!customValid) throw new Error("Every custom row needs a positive amount and a due date");
            settleBody.custom_schedule = customRows.map((r) => ({ amount: parseFloat(r.amount), due_date: r.due_date }));
          } else {
            if (!form.first_due_date) throw new Error("First due date is required for Option 2");
            settleBody.first_due_date = form.first_due_date;
            settleBody.frequency = form.frequency;
            const monthly = parseFloat(form.monthly_amount);
            if (!isNaN(monthly) && monthly > 0) settleBody.monthly_amount = monthly;
            else settleBody.installments = parseInt(form.installments, 10);
          }
        } else if (settlementOption === "option_custom_paid_in_full") {
          // Prefer explicit amount if the user typed one; server accepts either field.
          const amt = parseFloat(customAmount);
          if (!isNaN(amt) && amt > 0 && amt < balanceNum) {
            settleBody.custom_amount_due = amt;
          } else {
            const p = parseFloat(customPct);
            if (isNaN(p) || p <= 0 || p >= 100) throw new Error("Custom settlement: enter a discount 1–99% or an amount less than the balance");
            settleBody.custom_discount_pct = p;
          }
        }
        const { data } = await api.post(`/admin/properties/${property.id}/settlement/apply`, settleBody);
        const s = data?.active_settlement;
        const paidInFull = ["option1_65_paid_in_full", "option3_35_paid_in_full", "option_custom_paid_in_full"].includes(settlementOption);
        if (paidInFull) {
          const pct = s?.discount_pct ?? settlementPreview?.pct ?? "";
          toast.success(`${pct}% settlement applied · balance reduced by $${(s?.discount_amount || 0).toFixed(2)} · $${(s?.amount_due || 0).toFixed(2)} due in full`);
        } else {
          toast.success(`Option 2 applied · $${(s?.discount_amount || 0).toFixed(2)} discount · payment plan created for $${(s?.amount_due || 0).toFixed(2)}`);
        }
        setOpen(false);
        onDone?.();
        return;
      }

      // No settlement — regular arrangement flow
      const body = {
        property_id: property.id,
        notes: form.notes,
        initial_deposit: deposit,
        deposit_method: form.deposit_method,
        deposit_reference: form.deposit_reference,
      };
      if (mode === "custom") {
        if (!customValid) throw new Error("Every custom row needs a positive amount and a due date");
        body.custom_schedule = customRows.map((r) => ({ amount: parseFloat(r.amount), due_date: r.due_date }));
      } else {
        if (!form.total_amount || !form.first_due_date) throw new Error("Total and first due date required");
        const total = parseFloat(form.total_amount);
        if (deposit >= total) throw new Error("Deposit must be less than the total balance");
        body.total_amount = total;
        body.first_due_date = form.first_due_date;
        body.frequency = form.frequency;
        const monthly = parseFloat(form.monthly_amount);
        if (!isNaN(monthly) && monthly > 0) body.monthly_amount = monthly;
        else body.installments = parseInt(form.installments, 10);
      }
      await api.post("/admin/arrangements", body);
      toast.success(deposit > 0 ? `Arrangement created — $${deposit.toFixed(2)} deposit posted & homeowner notified` : "Arrangement created & homeowner notified");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); }
    finally { setBusy(false); }
  };

  const totalNum = parseFloat(form.total_amount) || 0;
  const depositNum = parseFloat(form.initial_deposit) || 0;
  const remainingNum = Math.max(0, totalNum - depositNum);
  const monthlyNum = parseFloat(form.monthly_amount);
  const useMonthly = !isNaN(monthlyNum) && monthlyNum > 0;
  const perInstallment = useMonthly
    ? monthlyNum.toFixed(2)
    : (remainingNum && form.installments ? (remainingNum / parseInt(form.installments, 10)).toFixed(2) : "—");
  const derivedInstallments = useMonthly && remainingNum ? Math.ceil(remainingNum / monthlyNum) : (parseInt(form.installments, 10) || "—");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="rounded-full border-[#C86650] text-[#C86650] hover:bg-[#C86650] hover:text-white"
            data-testid={`setup-arrangement-${property.id}`}
          >
            <CalendarClock className="w-4 h-4 mr-1" /> Arrange
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Set up payment arrangement</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#5C6A64]">
            <b>{property.address}</b> · Unit {property.unit_number} — {property.owner_name}
          </p>

          {/* Balance + settlement selector */}
          <div className="rounded-xl border border-[#EADCC8] bg-[#FDF9F1] p-4" data-testid="arr-balance-block">
            <div className="flex items-baseline justify-between mb-2">
              <p className="text-xs uppercase tracking-[0.2em] text-[#5C6A64]">Current balance</p>
              <p data-testid="arr-balance-value" className={`font-serif text-2xl ${balanceNum > 0 ? "text-[#C86650]" : "text-[#2C4C3B]"}`}>
                ${balanceNum.toFixed(2)}
              </p>
            </div>
            {existingSettlement ? (
              <div data-testid="arr-existing-settlement" className="text-xs text-[#5C6A64] mt-2 p-2 rounded-lg bg-white border border-[#EADCC8]">
                A settlement offer is already active on this property ({existingSettlement.option === "option1_65_paid_in_full" ? "Option 1" : "Option 2"} · {existingSettlement.status}). Cancel it from the property page before applying a new one.
              </div>
            ) : (
              <>
                <label className="text-xs uppercase tracking-[0.2em] text-[#5C6A64]">Apply settlement offer (optional)</label>
                <Select value={settlementOption} onValueChange={setSettlementOption}>
                  <SelectTrigger data-testid="arr-settlement-select" className="mt-1 rounded-xl bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No settlement — plain payment plan</SelectItem>
                    <SelectItem value="option1_65_paid_in_full">Option 1 — 65% reduction · paid in full</SelectItem>
                    <SelectItem value="option2_50_payment_plan">Option 2 — 50% reduction · payment plan</SelectItem>
                    <SelectItem value="option3_35_paid_in_full">Option 3 — 35% reduction · paid in full (pay 65% of balance)</SelectItem>
                    <SelectItem value="option_custom_paid_in_full">Custom settlement — paid in full (choose % or $)</SelectItem>
                  </SelectContent>
                </Select>
                {settlementOption === "option_custom_paid_in_full" && (
                  <div className="mt-3 grid grid-cols-2 gap-3" data-testid="arr-custom-settlement-inputs">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#5C6A64]">Discount %</label>
                      <div className="relative mt-1">
                        <Input
                          data-testid="arr-custom-settlement-pct"
                          type="number"
                          step="1"
                          min="1"
                          max="99"
                          value={customPct}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setCustomPct(raw);
                            const p = Math.max(1, Math.min(99, parseFloat(raw) || 0));
                            if (balanceNum > 0 && !isNaN(p)) {
                              setCustomAmount((balanceNum * (1 - p / 100)).toFixed(2));
                            }
                          }}
                          className="rounded-xl pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6A64]">%</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#5C6A64]">Amount due ($)</label>
                      <div className="relative mt-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C6A64]">$</span>
                        <Input
                          data-testid="arr-custom-settlement-amount"
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={customAmount}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setCustomAmount(raw);
                            const a = parseFloat(raw);
                            if (balanceNum > 0 && !isNaN(a) && a > 0 && a < balanceNum) {
                              const p = ((balanceNum - a) / balanceNum) * 100;
                              setCustomPct(p.toFixed(1));
                            }
                          }}
                          className="rounded-xl pl-7"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {settlementPreview && (
                  <div data-testid="arr-settlement-breakdown" className="mt-3 space-y-1 text-sm">
                    <div className="flex justify-between text-[#5C6A64]">
                      <span>Original past-due balance</span>
                      <span data-testid="arr-settlement-original" className="line-through">${balanceNum.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#2C4C3B]">
                      <span>{settlementPreview.pct}% reduction (discount)</span>
                      <span data-testid="arr-settlement-discount"><b>− ${settlementPreview.discount.toFixed(2)}</b></span>
                    </div>
                    <div className="flex justify-between items-baseline pt-2 border-t border-[#EADCC8]">
                      <span className="text-[#1A2421] font-medium">Amount due after settlement</span>
                      <span data-testid="arr-settlement-amount-due" className="font-serif text-xl text-[#C86650]"><b>${settlementPreview.amount_due.toFixed(2)}</b></span>
                    </div>
                    {(settlementOption === "option1_65_paid_in_full" || settlementOption === "option3_35_paid_in_full" || settlementOption === "option_custom_paid_in_full") && (
                      <p className="text-[11px] text-[#5C6A64] mt-2">
                        Paid in full{deadlineHint ? <> by <b>{deadlineHint}</b></> : null}. No installment schedule required — homeowner pays the settled amount online or by check.
                      </p>
                    )}
                    {settlementOption === "option2_50_payment_plan" && (
                      <p className="text-[11px] text-[#5C6A64] mt-2">
                        Payment plan below is applied to the settled amount (${settlementPreview.amount_due.toFixed(2)}). Uniform or custom schedule — custom rows must sum to the remaining amount after any deposit.
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Mode toggle + scheduling: hidden entirely when a "paid in full" settlement is applied (Option 1/3/Custom) */}
          {settlementOption !== "option1_65_paid_in_full" && settlementOption !== "option3_35_paid_in_full" && settlementOption !== "option_custom_paid_in_full" && (
            <>
          <div className="inline-flex rounded-full border border-[#D5CFC4] bg-[#FDF9F1] p-1" data-testid="arr-mode-toggle">
            <button
              type="button"
              data-testid="arr-mode-uniform"
              onClick={() => setMode("uniform")}
              className={`px-4 h-9 rounded-full text-sm font-medium transition-all ${mode === "uniform" ? "bg-[#2C4C3B] text-white" : "text-[#2C4C3B]"}`}
            >
              Uniform plan
            </button>
            <button
              type="button"
              data-testid="arr-mode-custom"
              onClick={() => setMode("custom")}
              className={`px-4 h-9 rounded-full text-sm font-medium transition-all ${mode === "custom" ? "bg-[#C86650] text-white" : "text-[#C86650]"}`}
            >
              Custom schedule
            </button>
          </div>

          {mode === "uniform" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Total amount ($)</label>
                  <Input data-testid="arr-total" type="number" step="0.01" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} className="mt-1 rounded-xl" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Installments</label>
                  <Input data-testid="arr-installments" type="number" min={2} max={24} value={form.installments} onChange={(e) => setForm({ ...form, installments: e.target.value })} className="mt-1 rounded-xl" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C6A64]">First due date</label>
                  <Input data-testid="arr-first-due" type="date" value={form.first_due_date} onChange={(e) => setForm({ ...form, first_due_date: e.target.value })} className="mt-1 rounded-xl" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Frequency</label>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger data-testid="arr-frequency" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="bi-weekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Monthly amount (optional · overrides installments)</label>
                <Input data-testid="arr-monthly" type="number" step="0.01" value={form.monthly_amount} onChange={(e) => setForm({ ...form, monthly_amount: e.target.value })} className="mt-1 rounded-xl" placeholder="e.g. 250.00" />
              </div>
              <div className="p-3 bg-[#FDF9F1] border border-[#EADCC8] rounded-xl text-sm text-[#2C4C3B]">
                Preview: <b>{derivedInstallments}</b> payments of <b>${perInstallment}</b> ({form.frequency}), starting <b>{form.first_due_date}</b>
                {depositNum > 0 && (
                  <div className="text-xs text-[#5C6A64] mt-1">
                    After ${depositNum.toFixed(2)} upfront deposit → ${remainingNum.toFixed(2)} scheduled
                  </div>
                )}
                {useMonthly && <div className="text-xs text-[#5C6A64] mt-1">Monthly amount overrides installments count.</div>}
              </div>
            </>
          ) : (
            <>
              <div className="rounded-xl border border-[#EADCC8] overflow-hidden" data-testid="arr-custom-rows">
                <table className="w-full text-sm">
                  <thead className="bg-[#EADCC8] text-[#2C4C3B]">
                    <tr>
                      <th className="text-left px-3 py-2 w-10">#</th>
                      <th className="text-left px-3 py-2">Amount ($)</th>
                      <th className="text-left px-3 py-2">Due date</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {customRows.map((r, i) => (
                      <tr key={`row-${i}-${r.due_date}`} className="border-t border-[#EADCC8]" data-testid={`arr-custom-row-${i}`}>
                        <td className="px-3 py-2 text-[#5C6A64]">{i + 1}</td>
                        <td className="px-3 py-2">
                          <Input data-testid={`arr-custom-amount-${i}`} type="number" step="0.01" min="0.01" value={r.amount} onChange={(e) => updateRow(i, "amount", e.target.value)} className="h-9 rounded-lg" placeholder="0.00" />
                        </td>
                        <td className="px-3 py-2">
                          <Input data-testid={`arr-custom-date-${i}`} type="date" value={r.due_date} onChange={(e) => updateRow(i, "due_date", e.target.value)} className="h-9 rounded-lg" />
                        </td>
                        <td className="px-2 py-2 text-right">
                          {customRows.length > 1 && (
                            <button type="button" data-testid={`arr-custom-remove-${i}`} onClick={() => removeRow(i)} className="text-[#C86650] hover:text-[#B4553F]" aria-label="Remove row">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-2 border-t border-[#EADCC8] bg-[#FDF9F1] flex justify-between items-center">
                  <button type="button" data-testid="arr-custom-add-row" onClick={addRow} className="text-sm text-[#C86650] hover:underline">
                    + Add installment
                  </button>
                  {(() => {
                    // For Option 2, show a live "sum vs target" pill so admin can see mismatch before submit.
                    const target = settlementOption === "option2_50_payment_plan" && settlementPreview
                      ? Math.round((settlementPreview.amount_due - depositNum) * 100) / 100
                      : null;
                    const mismatch = target !== null && Math.abs(customTotal - target) > 0.01;
                    return (
                      <span className="text-xs text-[#5C6A64]">
                        Total: <b className={mismatch ? "text-[#C86650]" : "text-[#2C4C3B]"} data-testid="arr-custom-sum">${customTotal.toFixed(2)}</b>
                        {target !== null && (
                          <> · Target: <b data-testid="arr-custom-target">${target.toFixed(2)}</b>{mismatch && <span className="text-[#C86650]"> · off by ${Math.abs(customTotal - target).toFixed(2)}</span>}</>
                        )}
                        <> · {customRows.length} installment{customRows.length !== 1 ? "s" : ""}</>
                      </span>
                    );
                  })()}
                </div>
              </div>
              <p className="text-[11px] text-[#5C6A64]">Custom schedules skip the frequency generator — dates and amounts are used exactly as entered. Any upfront deposit below is recorded as an extra payment.</p>
            </>
          )}
            </>
          )}

          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes (optional)</label>
            <Textarea data-testid="arr-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" placeholder="Terms, expectations, contact info…" />
          </div>
          {settlementOption !== "option1_65_paid_in_full" && settlementOption !== "option3_35_paid_in_full" && settlementOption !== "option_custom_paid_in_full" && (
          <div className="border-t border-[#EADCC8] pt-3">
            <p className="text-xs uppercase tracking-wider text-[#5C6A64] mb-2">Upfront deposit (optional)</p>
            <p className="text-xs text-[#5C6A64] mb-2">If the homeowner already paid a deposit, record it here — it posts against their balance immediately.</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Amount ($)</label>
                <Input data-testid="arr-deposit-amount" type="number" step="0.01" min="0" value={form.initial_deposit} onChange={(e) => setForm({ ...form, initial_deposit: e.target.value })} className="mt-1 rounded-xl" placeholder="0.00" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Method</label>
                <Select value={form.deposit_method} onValueChange={(v) => setForm({ ...form, deposit_method: v })}>
                  <SelectTrigger data-testid="arr-deposit-method" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                    <SelectItem value="money_order">Money order</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Reference / Check #</label>
                <Input data-testid="arr-deposit-ref" value={form.deposit_reference} onChange={(e) => setForm({ ...form, deposit_reference: e.target.value })} className="mt-1 rounded-xl" />
              </div>
            </div>
          </div>
          )}
          <Button
            data-testid="arr-submit"
            onClick={submit}
            disabled={busy || (settlementOption === "none" && mode === "custom" && !customValid) || (!!existingSettlement && settlementOption !== "none")}
            className={`w-full rounded-full ${settlementOption === "option1_65_paid_in_full" ? "bg-[#C86650] hover:bg-[#B4553F]" : "bg-[#2C4C3B] hover:bg-[#1F3D2E]"}`}
          >
            {busy
              ? (settlementOption === "none" ? "Creating…" : "Applying…")
              : settlementOption === "option1_65_paid_in_full"
                ? `Apply Option 1 — settle for $${(settlementPreview?.amount_due || 0).toFixed(2)}`
                : settlementOption === "option2_50_payment_plan"
                  ? `Apply Option 2 — plan on $${(settlementPreview?.amount_due || 0).toFixed(2)}`
                  : "Create arrangement"
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Recalculate remaining installments ----------
function RecalculateDialog({ arrangement, onDone }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const unpaidRows = (arrangement.schedule || []).filter((s) => s.status !== "paid");
  const currentUnpaidTotal = unpaidRows.reduce((s, r) => s + (r.amount || 0), 0);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {};
      const t = parseFloat(target);
      if (!isNaN(t) && t > 0) body.new_remaining_total = t;
      await api.post(`/admin/arrangements/${arrangement.id}/recalculate`, body);
      toast.success("Remaining installments redistributed");
      setOpen(false);
      setTarget("");
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`arr-recalc-${arrangement.id}`} className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white">
          Recalculate remaining
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Recalculate remaining installments</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#5C6A64]">
            <b>{arrangement.address}</b> · Unit {arrangement.unit_number}
          </p>
          <div className="p-3 rounded-xl bg-[#FDF9F1] border border-[#EADCC8] text-sm">
            <p>Unpaid installments: <b>{unpaidRows.length}</b></p>
            <p>Current unpaid total: <b className="text-[#C86650]">${currentUnpaidTotal.toFixed(2)}</b></p>
            <p className="text-[11px] text-[#5C6A64] mt-1">Paid installments and every due date will be kept exactly as-is. Only the unpaid amounts get redistributed proportionally.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">New remaining total ($)</label>
            <Input
              data-testid="arr-recalc-target"
              type="number"
              step="0.01"
              min="0.01"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              className="mt-1 rounded-xl"
              placeholder="Leave blank to auto-use current property balance"
            />
            <p className="text-[11px] text-[#5C6A64] mt-1">Blank = use the property's current outstanding balance from the ledger.</p>
          </div>
          <Button data-testid="arr-recalc-submit" onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2C4C3B]">
            {busy ? "Recalculating…" : "Redistribute"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ArrangementsTab({ properties, onReload }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active"); // active, completed, cancelled, all
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/arrangements");
      setItems(r.data);
    } catch { toast.error("Failed to load arrangements"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = items.filter((a) => {
    if (filter !== "all" && a.status !== filter) return false;
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = `${a.owner_name || ""} ${a.owner_email || ""} ${a.address || ""} ${a.unit_number || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const markPaid = async (arr_id, n) => {
    try {
      await api.post(`/admin/arrangements/${arr_id}/installments/${n}/pay`);
      toast.success(`Installment ${n} marked paid`);
      load();
      onReload?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cancel = async (arr_id) => {
    if (!window.confirm("Cancel this arrangement? Existing payments remain in the ledger.")) return;
    try { await api.post(`/admin/arrangements/${arr_id}/cancel`); toast.success("Cancelled"); load(); }
    catch { toast.error("Failed"); }
  };
  const remind = async (arr_id) => {
    try {
      const { data } = await api.post(`/admin/arrangements/${arr_id}/remind`);
      toast.success(data.overdue > 0 ? `Reminder sent (${data.overdue} overdue)` : `Reminder sent (${data.unpaid} unpaid)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send reminder"); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="arr-filter" className="w-56 rounded-xl bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Input
            data-testid="arrangement-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, address, unit…"
            className="w-64 rounded-xl bg-white"
          />
        </div>
        <p className="text-sm text-[#5C6A64]">
          Tip: Start a new arrangement from the <b>Delinquency</b> tab → "Arrange" button next to a property.
        </p>
      </div>

      {loading ? <p className="text-[#5C6A64]">Loading…</p> : visible.length === 0 ? (
        <Card className="rounded-2xl border-[#D5CFC4]"><CardContent className="p-12 text-center text-[#5C6A64]">
          No {filter === "all" ? "" : filter} arrangements.
        </CardContent></Card>
      ) : (
        <ArrangementsByProperty
          items={visible}
          onMarkPaid={markPaid}
          onCancel={cancel}
          onReminder={remind}
          reload={load}
        />
      )}
    </div>
  );
}

/**
 * Groups arrangements by property. Renders one collapsible property card;
 * clicking expands to show that property's arrangement details (schedule, actions).
 */
function ArrangementsByProperty({ items, onMarkPaid, onCancel, onReminder, reload }) {
  const [expanded, setExpanded] = useState(() => new Set());
  // Group by property_id (fallback to address+unit if missing).
  const groups = React.useMemo(() => {
    const map = new Map();
    items.forEach((a) => {
      const key = a.property_id || `${a.address}#${a.unit_number}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          property_id: a.property_id,
          address: a.address,
          unit_number: a.unit_number,
          owner_name: a.owner_name,
          owner_email: a.owner_email,
          arrangements: [],
        });
      }
      map.get(key).arrangements.push(a);
    });
    // Sort each group's arrangements newest-first; sort groups by unit#.
    map.forEach((g) => g.arrangements.sort((x, y) => new Date(y.created_at || 0) - new Date(x.created_at || 0)));
    return Array.from(map.values()).sort((a, b) => String(a.unit_number || "").localeCompare(String(b.unit_number || ""), undefined, { numeric: true }));
  }, [items]);

  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <div className="space-y-3" data-testid="arr-by-property">
      {groups.map((g) => {
        const isOpen = expanded.has(g.key);
        const activeCount = g.arrangements.filter((a) => a.status === "active").length;
        const settlementCount = g.arrangements.filter((a) => a.settlement_option === "option2_50_payment_plan").length;
        const totalOutstanding = g.arrangements
          .filter((a) => a.status === "active")
          .reduce((sum, a) => {
            const paid = (a.schedule || []).filter((s) => s.status === "paid").reduce((s2, s) => s2 + (s.amount || 0), 0);
            return sum + Math.max(0, (a.total_amount || 0) - paid);
          }, 0);
        return (
          <Card key={g.key} className="rounded-2xl border-[#D5CFC4]" data-testid={`arr-group-${g.property_id || g.key}`}>
            <button
              type="button"
              onClick={() => toggle(g.key)}
              className="w-full text-left"
              data-testid={`arr-group-toggle-${g.property_id || g.key}`}
            >
              <CardContent className="p-5 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-serif text-lg text-[#1A2421]">{g.address} · Unit {g.unit_number}</p>
                    <Badge className="bg-[#EADCC8] text-[#2C4C3B]" data-testid={`arr-group-count-${g.property_id || g.key}`}>
                      {g.arrangements.length} arrangement{g.arrangements.length !== 1 ? "s" : ""}
                    </Badge>
                    {activeCount > 0 && <Badge className="bg-[#C86650]">{activeCount} active</Badge>}
                    {settlementCount > 0 && (
                      <Badge className="bg-white text-[#2C4C3B] border border-[#2C4C3B]">
                        {settlementCount} settlement
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-[#5C6A64] mt-1 truncate">{g.owner_name}{g.owner_email ? ` · ${g.owner_email}` : ""}</p>
                </div>
                <div className="flex items-center gap-4">
                  {activeCount > 0 && (
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider text-[#5C6A64]">Active outstanding</p>
                      <p className="font-serif text-lg text-[#C86650]">${totalOutstanding.toFixed(2)}</p>
                    </div>
                  )}
                  {g.property_id && (
                    <Link
                      to={`/admin/properties/${g.property_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs text-[#2C4C3B] underline hover:text-[#C86650]"
                      data-testid={`arr-group-open-${g.property_id}`}
                    >
                      Open property →
                    </Link>
                  )}
                  <span className={`text-xs text-[#5C6A64] transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
                </div>
              </CardContent>
            </button>
            {isOpen && (
              <div className="border-t border-[#EADCC8] p-4 space-y-3 bg-[#FDF9F1]">
                {g.arrangements.map((a) => (
                  <ArrangementCard
                    key={a.id}
                    a={a}
                    onMarkPaid={onMarkPaid}
                    onCancel={onCancel}
                    onReminder={onReminder}
                    reload={reload}
                  />
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

/** Extracted card render — used inside the expanded property group. */
function ArrangementCard({ a, onMarkPaid, onCancel, onReminder, reload }) {
  const paidCount = (a.schedule || []).filter((s) => s.status === "paid").length;
  const paidAmt = (a.schedule || []).filter((s) => s.status === "paid").reduce((sum, s) => sum + (s.amount || 0), 0);
  const outstanding = a.total_amount - paidAmt;
  return (
    <Card data-testid={`arr-card-${a.id}`} className="rounded-2xl border-[#D5CFC4] bg-white">
      <CardContent className="p-5">
        <div className="flex justify-between items-start flex-wrap gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={a.status === "active" ? "bg-[#C86650]" : a.status === "completed" ? "bg-[#2C4C3B]" : "bg-[#5C6A64]"}>
                {a.status}
              </Badge>
              {a.settlement_option === "option2_50_payment_plan" && (
                <Badge data-testid={`arr-settlement-badge-${a.id}`} className="bg-[#EADCC8] text-[#2C4C3B] border border-[#2C4C3B]">
                  Settlement · 50% off
                </Badge>
              )}
              {a.is_custom_schedule && (
                <Badge data-testid={`arr-custom-badge-${a.id}`} variant="outline" className="border-[#C86650] text-[#C86650]">
                  Custom schedule
                </Badge>
              )}
              <span className="text-xs text-[#5C6A64]">
                {a.installments} × {a.frequency} · Started {a.first_due_date}
              </span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-[#5C6A64] uppercase tracking-wider">Progress</p>
            <p className="font-serif text-xl text-[#2C4C3B]">${paidAmt.toFixed(2)} <span className="text-sm text-[#5C6A64]">/ ${(a.total_amount || 0).toFixed(2)}</span></p>
            <p className="text-xs text-[#5C6A64]">{paidCount} / {a.installments} paid · ${outstanding.toFixed(2)} left</p>
          </div>
        </div>
        {a.notes && <p className="text-sm text-[#5C6A64] italic mb-3">"{a.notes}"</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {(a.schedule || []).map((s) => {
            const overdue = s.status === "pending" && s.due_date < new Date().toISOString().slice(0, 10);
            return (
              <div
                key={s.n}
                data-testid={`arr-inst-${a.id}-${s.n}`}
                className={`flex items-center justify-between rounded-xl border p-3 text-sm ${
                  s.status === "paid" ? "bg-[#F9F8F6] border-[#D5CFC4]" :
                  overdue ? "bg-[#FDF3F0] border-[#C86650]" : "bg-white border-[#EADCC8]"
                }`}
              >
                <div>
                  <p className="text-[#1A2421]">
                    #{s.n} — <b>${s.amount.toFixed(2)}</b>
                  </p>
                  <p className="text-xs text-[#5C6A64]">Due {s.due_date}{overdue && " · overdue"}</p>
                </div>
                {s.status === "paid" ? (
                  <Badge className="bg-[#2C4C3B]">Paid</Badge>
                ) : a.status === "active" ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full border-[#2C4C3B] text-[#2C4C3B]"
                    data-testid={`arr-mark-paid-${a.id}-${s.n}`}
                    onClick={() => onMarkPaid(a.id, s.n)}
                  >
                    Mark paid
                  </Button>
                ) : (
                  <Badge variant="outline">Pending</Badge>
                )}
              </div>
            );
          })}
        </div>
        {a.status === "active" && (
          <div className="flex justify-end gap-2 mt-3">
            <RecalculateDialog arrangement={a} onDone={reload} />
            <Button size="sm" variant="ghost" onClick={() => onCancel(a.id)} data-testid={`arr-cancel-${a.id}`} className="text-[#C86650]">
              Cancel arrangement
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}






// ---------- Documents Tab ----------
const DOC_CATEGORIES = ["Bylaws", "Rules", "Minutes", "Financials", "General"];

function VolunteersTab() {
  const [data, setData] = useState(null); // { event, rsvps, totals }
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/events/labor-day/rsvps");
      setData(r.data);
    } catch { toast.error("Failed to load RSVPs"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this RSVP entry?")) return;
    try {
      await api.delete(`/admin/events/labor-day/rsvps/${id}`);
      toast.success("Removed");
      load();
    } catch { toast.error("Delete failed"); }
  };

  if (loading) return <p className="text-[#5C6A64] py-8" data-testid="volunteers-loading">Loading RSVPs…</p>;
  if (!data) return null;

  const { event, rsvps, totals } = data;
  const roleLabels = Object.fromEntries(event.volunteer_roles.map((r) => [r.key, r.label]));

  const visible = roleFilter === "all"
    ? rsvps
    : roleFilter === "attending"
      ? rsvps.filter((r) => (r.attendee_count || 0) > 0)
      : rsvps.filter((r) => (r.volunteer_roles || []).includes(roleFilter));

  const downloadCsv = () => {
    const url = `${BACKEND}/api/admin/events/labor-day/rsvps.csv`;
    fetch(url, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error("csv");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "labor-day-rsvps.csv";
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("CSV download failed"));
  };

  return (
    <div className="mt-4" data-testid="volunteers-tab">
      <div className="mb-4 flex justify-between items-start flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#C86650]">{event.date_label} · {event.location}</p>
          <h2 className="font-serif text-2xl text-[#1A2421]">{event.title} — RSVPs</h2>
        </div>
        <Button
          onClick={downloadCsv}
          data-testid="volunteers-csv-btn"
          size="sm"
          variant="outline"
          className="rounded-full border-[#2C4C3B] text-[#2C4C3B]"
        >
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <Card className="rounded-2xl border-[#D5CFC4]" data-testid="stat-attendees">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#5C6A64] tracking-wider">Attendees</p>
            <p className="font-serif text-3xl text-[#2C4C3B]">{totals.attendees}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-[#D5CFC4]" data-testid="stat-households">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#5C6A64] tracking-wider">Households</p>
            <p className="font-serif text-3xl text-[#2C4C3B]">{totals.households}</p>
          </CardContent>
        </Card>
        {event.volunteer_roles.map((r) => (
          <Card
            key={r.key}
            data-testid={`stat-role-${r.key}`}
            className="rounded-2xl border-[#D5CFC4] bg-[#FDF9F1]"
          >
            <CardContent className="p-4">
              <p className="text-xs uppercase text-[#C86650] tracking-wider">{r.label}</p>
              <p className="font-serif text-3xl text-[#2C4C3B]">{totals.roles[r.key] || 0}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 flex-wrap mb-4">
        {[
          { key: "all", label: `All (${rsvps.length})` },
          { key: "attending", label: `Attending (${rsvps.filter((r) => (r.attendee_count || 0) > 0).length})` },
          ...event.volunteer_roles.map((r) => ({ key: r.key, label: `${r.label} (${totals.roles[r.key] || 0})` })),
        ].map((chip) => (
          <Button
            key={chip.key}
            size="sm"
            variant={roleFilter === chip.key ? "default" : "outline"}
            onClick={() => setRoleFilter(chip.key)}
            data-testid={`volunteers-filter-${chip.key}`}
            className={roleFilter === chip.key ? "rounded-full bg-[#2C4C3B] hover:bg-[#233D2F]" : "rounded-full border-[#D5CFC4]"}
          >
            {chip.label}
          </Button>
        ))}
      </div>

      {visible.length === 0 ? (
        <p className="text-[#5C6A64] py-8" data-testid="volunteers-empty">
          {rsvps.length === 0 ? "No RSVPs yet." : "No RSVPs match this filter."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[#D5CFC4] bg-white">
          <table className="w-full text-sm" data-testid="volunteers-table">
            <thead className="bg-[#FDF9F1] text-[#5C6A64]">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Property</th>
                <th className="text-center px-4 py-3 font-medium">Attendees</th>
                <th className="text-left px-4 py-3 font-medium">Volunteer</th>
                <th className="text-left px-4 py-3 font-medium">Note</th>
                <th className="text-left px-4 py-3 font-medium">Submitted</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} data-testid={`rsvp-row-${r.id}`} className="border-t border-[#EFEBE3] hover:bg-[#FDF9F1]">
                  <td className="px-4 py-3">
                    <p className="font-medium text-[#1A2421]">{r.user_name}</p>
                    <p className="text-xs text-[#5C6A64]">{r.user_email}</p>
                  </td>
                  <td className="px-4 py-3 text-[#1A2421]">
                    {r.property_address ? (
                      <>
                        {r.property_address}
                        {r.unit_number ? <span className="text-xs text-[#5C6A64] block">Unit {r.unit_number}</span> : null}
                      </>
                    ) : <span className="text-xs text-[#5C6A64]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center font-medium">
                    {r.attendee_count > 0
                      ? <span className="text-[#2C4C3B]">{r.attendee_count}</span>
                      : <Badge variant="outline" className="border-[#5C6A64] text-[#5C6A64] text-[10px]">Not attending</Badge>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(r.volunteer_roles || []).length === 0 && <span className="text-xs text-[#5C6A64]">—</span>}
                      {(r.volunteer_roles || []).map((k) => (
                        <Badge key={k} className="bg-[#C86650] text-white text-[10px]">
                          {roleLabels[k] || k}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#5C6A64] max-w-xs">
                    {r.other_note || <span className="text-[#B7B0A2]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#5C6A64] whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => remove(r.id)}
                      data-testid={`rsvp-delete-${r.id}`}
                      className="rounded-full border-[#C86650] text-[#C86650] hover:bg-[#C86650] hover:text-white h-8"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CommunityInterestsTab() {
  const [data, setData] = useState(null); // { rows, totals }
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [optionFilter, setOptionFilter] = useState("all"); // sub-filter within a chosen category
  const [flagFilter, setFlagFilter] = useState("all"); // all | lead_teach | with_idea | with_skills
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/community-interests");
      setData(r.data);
    } catch { toast.error("Failed to load community interests"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this sign-up?")) return;
    try {
      await api.delete(`/admin/community-interests/${id}`);
      toast.success("Removed");
      setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
      load();
    } catch { toast.error("Delete failed"); }
  };

  const downloadCsv = () => {
    fetch(`${BACKEND}/api/admin/community-interests.csv`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error("csv"); return r.blob(); })
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "community-interests.csv";
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => toast.error("CSV download failed"));
  };

  if (loading) return <p className="text-[#5C6A64] py-8" data-testid="interests-loading">Loading sign-ups…</p>;
  if (!data) return null;

  const { rows, totals } = data;

  // Category filter drives an option sub-picker.
  const activeCategory = INTEREST_CATEGORIES.find((c) => c.key === categoryFilter);
  const filtered = rows.filter((r) => {
    if (flagFilter === "lead_teach" && !((r.interests?.wellness || []).includes("teach_lead_activity") || (r.participation || []).includes("lead"))) return false;
    if (flagFilter === "with_idea" && !(r.activity_idea || "").trim()) return false;
    if (flagFilter === "with_skills" && !(r.skills || "").trim()) return false;
    if (categoryFilter !== "all") {
      const opts = r.interests?.[categoryFilter] || [];
      if (opts.length === 0) return false;
      if (optionFilter !== "all" && !opts.includes(optionFilter)) return false;
    }
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = `${r.first_name || ""} ${r.last_name || ""} ${r.email || ""} ${r.phone || ""} ${r.neighborhood || ""} ${r.street || ""} ${r.activity_idea || ""} ${r.skills || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const selectAllVisible = () => setSelected(new Set(filtered.map((r) => r.id)));
  const clearSelection = () => setSelected(new Set());

  const composeMailto = () => {
    const recipients = filtered
      .filter((r) => selected.has(r.id) && r.email)
      .map((r) => r.email);
    const emails = recipients.length > 0
      ? recipients
      : filtered.map((r) => r.email).filter(Boolean);
    if (emails.length === 0) return toast.error("No emails to contact");
    const subj = activeCategory
      ? `Community update: ${activeCategory.title}`
      : "Community update from your HOA";
    // Use BCC to protect neighbor privacy.
    window.location.href = `mailto:?bcc=${encodeURIComponent(emails.join(","))}&subject=${encodeURIComponent(subj)}`;
  };

  const copyEmails = async () => {
    const recipients = filtered
      .filter((r) => selected.has(r.id) && r.email)
      .map((r) => r.email);
    const emails = recipients.length > 0
      ? recipients
      : filtered.map((r) => r.email).filter(Boolean);
    if (emails.length === 0) return toast.error("No emails to copy");
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      toast.success(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"} to clipboard`);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="mt-4" data-testid="interests-tab">
      <div className="mb-4 flex justify-between items-start flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#C86650]">Get Involved · Sign-ups</p>
          <h2 className="font-serif text-2xl text-[#1A2421]">Community volunteer interests</h2>
          <p className="text-sm text-[#5C6A64] mt-1">Casual volunteer interests submitted from the public Get Involved page.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={copyEmails} data-testid="interests-copy-emails" size="sm" variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B]">
            <Paperclip className="w-4 h-4 mr-2" /> Copy emails
          </Button>
          <Button onClick={composeMailto} data-testid="interests-mailto" size="sm" variant="outline" className="rounded-full border-[#C86650] text-[#C86650]">
            <MailWarning className="w-4 h-4 mr-2" /> Compose email
          </Button>
          <Button onClick={downloadCsv} data-testid="interests-csv" size="sm" className="rounded-full bg-[#2C4C3B] hover:bg-[#233D2F]">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="rounded-2xl border-[#D5CFC4]" data-testid="stat-total-signups">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#5C6A64] tracking-wider">Total sign-ups</p>
            <p className="font-serif text-3xl text-[#2C4C3B]">{totals.total}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-[#D5CFC4] bg-[#FDF9F1]" data-testid="stat-lead-teach">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#C86650] tracking-wider flex items-center gap-1">
              <Star className="w-3 h-3" /> Interested in leading/teaching
            </p>
            <p className="font-serif text-3xl text-[#2C4C3B]">{totals.lead_teach}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-[#D5CFC4] bg-[#FDF9F1]" data-testid="stat-with-idea">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#C86650] tracking-wider flex items-center gap-1">
              <Lightbulb className="w-3 h-3" /> Submitted an idea
            </p>
            <p className="font-serif text-3xl text-[#2C4C3B]">{totals.with_idea}</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-[#D5CFC4] bg-[#FDF9F1]" data-testid="stat-with-skills">
          <CardContent className="p-4">
            <p className="text-xs uppercase text-[#C86650] tracking-wider flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Skills to share
            </p>
            <p className="font-serif text-3xl text-[#2C4C3B]">{totals.with_skills}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap mb-3 items-center">
        <span className="text-xs uppercase tracking-wider text-[#5C6A64] mr-1">Category:</span>
        <Button
          size="sm"
          variant={categoryFilter === "all" ? "default" : "outline"}
          onClick={() => { setCategoryFilter("all"); setOptionFilter("all"); }}
          data-testid="interests-cat-all"
          className={categoryFilter === "all" ? "rounded-full bg-[#2C4C3B]" : "rounded-full border-[#D5CFC4]"}
        >All ({totals.total})</Button>
        {INTEREST_CATEGORIES.map((c) => {
          // Short chip label — first two words for readability, unique per category.
          const short = c.key === "social" ? "Social" :
                        c.key === "decor" ? "Décor" :
                        c.key === "wellness" ? "Fitness" :
                        c.key === "family" ? "Family" :
                        "Volunteer Team";
          return (
          <Button
            key={c.key}
            size="sm"
            variant={categoryFilter === c.key ? "default" : "outline"}
            onClick={() => { setCategoryFilter(c.key); setOptionFilter("all"); }}
            data-testid={`interests-cat-${c.key}`}
            className={categoryFilter === c.key ? "rounded-full bg-[#C86650] hover:bg-[#B4553F]" : "rounded-full border-[#D5CFC4]"}
          >
            {short} ({totals.by_category?.[c.key] || 0})
          </Button>
          );
        })}
      </div>
      {activeCategory && (
        <div className="flex gap-2 flex-wrap mb-3 items-center pl-4 border-l-2 border-[#EADCC8]">
          <span className="text-xs uppercase tracking-wider text-[#5C6A64] mr-1">Interest:</span>
          <Button
            size="sm"
            variant={optionFilter === "all" ? "default" : "outline"}
            onClick={() => setOptionFilter("all")}
            className={optionFilter === "all" ? "rounded-full bg-[#2C4C3B] text-[10px] h-7" : "rounded-full border-[#D5CFC4] text-[10px] h-7"}
          >Any</Button>
          {activeCategory.options.map((o) => (
            <Button
              key={o.key}
              size="sm"
              variant={optionFilter === o.key ? "default" : "outline"}
              onClick={() => setOptionFilter(o.key)}
              className={optionFilter === o.key ? "rounded-full bg-[#C86650] text-[10px] h-7" : "rounded-full border-[#D5CFC4] text-[10px] h-7"}
            >{o.label}</Button>
          ))}
        </div>
      )}
      <div className="flex gap-2 flex-wrap mb-4 items-center">
        <span className="text-xs uppercase tracking-wider text-[#5C6A64] mr-1">Flag:</span>
        {[
          { key: "all", label: "All" },
          { key: "lead_teach", label: "Wants to lead/teach" },
          { key: "with_idea", label: "Has an idea" },
          { key: "with_skills", label: "Skills to share" },
        ].map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant={flagFilter === f.key ? "default" : "outline"}
            onClick={() => setFlagFilter(f.key)}
            data-testid={`interests-flag-${f.key}`}
            className={flagFilter === f.key ? "rounded-full bg-[#2C4C3B]" : "rounded-full border-[#D5CFC4]"}
          >{f.label}</Button>
        ))}
        <Input
          data-testid="interests-search"
          placeholder="Search name / email / neighborhood…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="ml-auto max-w-xs rounded-xl"
        />
      </div>

      {/* Selection actions */}
      {filtered.length > 0 && (
        <div className="mb-3 flex items-center gap-3 text-xs text-[#5C6A64]" data-testid="interests-selection-bar">
          <span>{selected.size > 0 ? `${selected.size} selected` : `${filtered.length} shown`}</span>
          <button type="button" onClick={selectAllVisible} className="underline hover:text-[#C86650]">Select all shown</button>
          {selected.size > 0 && <button type="button" onClick={clearSelection} className="underline hover:text-[#C86650]">Clear</button>}
        </div>
      )}

      {filtered.length === 0 ? (
        <p className="text-[#5C6A64] py-8" data-testid="interests-empty">
          {rows.length === 0 ? "No sign-ups yet." : "No sign-ups match this filter."}
        </p>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <Card key={r.id} data-testid={`interest-row-${r.id}`} className="rounded-2xl border-[#D5CFC4]">
              <CardContent className="p-5">
                <div className="flex justify-between items-start gap-3 flex-wrap">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleSelect(r.id)}
                      className="mt-1 w-4 h-4 accent-[#C86650]"
                      data-testid={`interest-select-${r.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <p className="font-medium text-[#1A2421]">{r.first_name} {r.last_name}</p>
                        <a href={`mailto:${r.email}`} className="text-sm text-[#C86650] hover:underline">{r.email}</a>
                        {r.phone && <span className="text-sm text-[#5C6A64]">· {r.phone}</span>}
                      </div>
                      <p className="text-xs text-[#5C6A64] mt-0.5">
                        {r.neighborhood || "—"}{r.street && ` · ${r.street}`}
                        {r.property_address && !r.street && <span className="ml-1 text-[#2C4C3B]">(profile: {r.property_address})</span>}
                        <span className="ml-2">· submitted {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
                      </p>

                      {/* Interests grouped by category */}
                      {Object.keys(r.interests || {}).length > 0 && (
                        <div className="mt-3 space-y-2">
                          {Object.entries(r.interests).map(([cat, opts]) => (
                            <div key={cat}>
                              <p className="text-[10px] uppercase tracking-wider text-[#5C6A64]">{CATEGORY_LABEL_BY_KEY[cat] || cat}</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {(opts || []).map((o) => (
                                  <Badge key={o} className="bg-[#EADCC8] text-[#2C4C3B] text-[10px]">
                                    {OPTION_LABEL_BY_KEY[o] || o}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {(r.participation || []).length > 0 && (
                        <div className="mt-3">
                          <p className="text-[10px] uppercase tracking-wider text-[#5C6A64]">Participation</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {r.participation.map((p) => (
                              <Badge key={p} className="bg-[#C86650] text-white text-[10px]">
                                {PARTICIPATION_LABEL_BY_KEY[p] || p}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {r.activity_idea && (
                        <div className="mt-3 p-3 rounded-xl bg-[#FDF9F1] border border-[#EADCC8]" data-testid={`interest-idea-${r.id}`}>
                          <p className="text-[10px] uppercase tracking-wider text-[#C86650] flex items-center gap-1">
                            <Lightbulb className="w-3 h-3" /> Idea
                          </p>
                          <p className="text-sm text-[#1A2421] mt-1 whitespace-pre-wrap">{r.activity_idea}</p>
                        </div>
                      )}

                      {r.skills && (
                        <div className="mt-2 p-3 rounded-xl bg-white border border-[#D5CFC4]" data-testid={`interest-skills-${r.id}`}>
                          <p className="text-[10px] uppercase tracking-wider text-[#5C6A64] flex items-center gap-1">
                            <Sparkles className="w-3 h-3" /> Skills / experience
                          </p>
                          <p className="text-sm text-[#1A2421] mt-1 whitespace-pre-wrap">{r.skills}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => remove(r.id)}
                    data-testid={`interest-delete-${r.id}`}
                    className="rounded-full border-[#C86650] text-[#C86650] hover:bg-[#C86650] hover:text-white h-8"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentsTab() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/documents");
      setDocs(r.data);
    } catch { toast.error("Failed to load documents"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const visible = filter === "All" ? docs : docs.filter((d) => d.category === filter);

  const remove = async (id) => {
    if (!window.confirm("Delete this document permanently?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex flex-wrap gap-2">
          {["All", ...DOC_CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`admin-doc-filter-${c.toLowerCase()}`}
              onClick={() => setFilter(c)}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                filter === c ? "bg-[#2C4C3B] text-white border-[#2C4C3B]" : "bg-white border-[#D5CFC4] text-[#1A2421] hover:bg-[#EADCC8]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-new-doc-btn" className="rounded-full bg-[#2C4C3B]">
              <Plus className="w-4 h-4 mr-2" /> Add Document
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Add Document</DialogTitle></DialogHeader>
            <AdminDocForm onDone={() => { setOpen(false); load(); }} />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? <p className="text-[#5C6A64]">Loading…</p> : visible.length === 0 ? (
        <Card className="rounded-2xl border-[#D5CFC4]"><CardContent className="p-12 text-center text-[#5C6A64]">
          No documents{filter !== "All" && ` in ${filter}`} yet.
        </CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((d) => (
            <Card key={d.id} data-testid={`admin-doc-card-${d.id}`} className="rounded-2xl border-[#D5CFC4]">
              <CardContent className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#EADCC8] flex items-center justify-center">
                    <FileText className="w-5 h-5 text-[#2C4C3B]" />
                  </div>
                  <Badge className="bg-[#2C4C3B]">{d.category}</Badge>
                </div>
                <p className="font-serif text-lg text-[#1A2421]">{d.title}</p>
                {d.description && <p className="text-sm text-[#5C6A64] mt-1 line-clamp-2">{d.description}</p>}
                {d.original_filename && <p className="text-xs text-[#5C6A64] mt-1">{d.original_filename} · {((d.size || 0) / 1024).toFixed(0)} KB</p>}
                <div className="flex justify-between items-center mt-3">
                  <a href={d.storage_path ? `${BACKEND}/api/documents/download/${d.storage_path}` : d.url} target="_blank" rel="noreferrer" data-testid={`admin-doc-download-${d.id}`}>
                    <Button variant="outline" size="sm" className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white">
                      <Download className="w-4 h-4 mr-1" /> Download
                    </Button>
                  </a>
                  <Button size="icon" variant="ghost" data-testid={`admin-doc-delete-${d.id}`} onClick={() => remove(d.id)}>
                    <Trash2 className="w-4 h-4 text-[#C86650]" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function AdminDocForm({ onDone }) {
  const [mode, setMode] = useState("upload"); // "upload" | "link"
  const [form, setForm] = useState({ title: "", url: "", category: "General", description: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.title) return toast.error("Title required");
    setBusy(true);
    try {
      if (mode === "upload") {
        if (!file) { setBusy(false); return toast.error("File required"); }
        if (file.size > 20 * 1024 * 1024) { setBusy(false); return toast.error("Max 20 MB"); }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title);
        fd.append("category", form.category);
        fd.append("description", form.description);
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Document uploaded");
      } else {
        if (!form.url) { setBusy(false); return toast.error("URL required"); }
        await api.post("/documents", form);
        toast.success("Document added");
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 p-1 bg-[#EADCC8] rounded-full">
        <button
          data-testid="admin-doc-mode-upload"
          onClick={() => setMode("upload")}
          className={`rounded-full py-2 text-sm ${mode === "upload" ? "bg-white text-[#2C4C3B] font-medium" : "text-[#5C6A64]"}`}
        >
          <Upload className="w-4 h-4 inline mr-1" /> Upload file
        </button>
        <button
          data-testid="admin-doc-mode-link"
          onClick={() => setMode("link")}
          className={`rounded-full py-2 text-sm ${mode === "link" ? "bg-white text-[#2C4C3B] font-medium" : "text-[#5C6A64]"}`}
        >
          <Link2 className="w-4 h-4 inline mr-1" /> Link URL
        </button>
      </div>
      <Input data-testid="admin-doc-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
      <Textarea data-testid="admin-doc-desc" rows={2} placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
        <SelectTrigger data-testid="admin-doc-category"><SelectValue /></SelectTrigger>
        <SelectContent>{DOC_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
      </Select>
      {mode === "upload" ? (
        <>
          <input
            data-testid="admin-doc-file"
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
            onChange={(e) => setFile(e.target.files?.[0])}
            className="block w-full text-sm text-[#1A2421] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-[#2C4C3B] file:text-white hover:file:bg-[#233D2F]"
          />
          {file && <p className="text-xs text-[#5C6A64]">{file.name} · {(file.size / 1024).toFixed(0)} KB</p>}
        </>
      ) : (
        <Input data-testid="admin-doc-url" placeholder="https://…" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
      )}
      <Button data-testid="admin-doc-submit" onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2C4C3B]">
        {busy ? "Saving…" : mode === "upload" ? "Upload document" : "Add link"}
      </Button>
    </div>
  );
}




function AmenitiesTab({ amenities, onReload }) {
  const [edits, setEdits] = useState({});
  useEffect(() => {
    const map = {};
    amenities.forEach((a) => { map[a.id] = { ...a }; });
    setEdits(map);
  }, [amenities]);

  const save = async (id) => {
    const e = edits[id];
    try {
      await api.patch(`/admin/amenities/${id}`, {
        key: e.key, name: e.name, price: parseFloat(e.price) || 0,
        description: e.description || "", active: !!e.active,
      });
      toast.success("Amenity updated");
      onReload();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="mt-4">
      <p className="text-sm text-[#5C6A64] mb-4">
        These prices and descriptions are what homeowners see on the Amenities and Booking pages.
      </p>
      <div className="grid md:grid-cols-2 gap-4">
        {amenities.map((a) => {
          const e = edits[a.id] || a;
          return (
            <Card key={a.id} data-testid={`amenity-edit-${a.key}`} className="rounded-2xl border-[#D5CFC4]">
              <CardContent className="p-6 space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Name</label>
                  <Input
                    data-testid={`amenity-name-${a.key}`}
                    value={e.name || ""}
                    onChange={(ev) => setEdits({ ...edits, [a.id]: { ...e, name: ev.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Price ($)</label>
                  <Input
                    data-testid={`amenity-price-${a.key}`}
                    type="number"
                    step="0.01"
                    value={e.price ?? ""}
                    onChange={(ev) => setEdits({ ...edits, [a.id]: { ...e, price: ev.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Description</label>
                  <Textarea
                    data-testid={`amenity-desc-${a.key}`}
                    rows={2}
                    value={e.description || ""}
                    onChange={(ev) => setEdits({ ...edits, [a.id]: { ...e, description: ev.target.value } })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    data-testid={`amenity-active-${a.key}`}
                    type="checkbox"
                    checked={e.active !== false}
                    onChange={(ev) => setEdits({ ...edits, [a.id]: { ...e, active: ev.target.checked } })}
                  />
                  Active (visible to homeowners)
                </label>
                <Button
                  data-testid={`amenity-save-${a.key}`}
                  onClick={() => save(a.id)}
                  className="w-full rounded-full bg-[#2C4C3B]"
                >
                  <Save className="w-4 h-4 mr-2" /> Save changes
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function ViewPaymentsButton({ propertyId, unit }) {
  const [open, setOpen] = useState(false);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/properties/${propertyId}/payments`);
      setTxs(r.data);
    } catch {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  };

  const onOpen = (v) => {
    setOpen(v);
    if (v) load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" data-testid={`view-payments-${propertyId}`}>
          <Receipt className="w-4 h-4 text-[#5C6A64]" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Payments — Unit {unit}</DialogTitle>
        </DialogHeader>
        <div className="mt-2">
          {loading && <p className="text-sm text-[#5C6A64]">Loading…</p>}
          {!loading && txs.length === 0 && <p className="text-sm text-[#5C6A64]">No payments recorded yet.</p>}
          {!loading && txs.length > 0 && (
            <div className="max-h-[400px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-[#5C6A64] border-b border-[#D5CFC4]">
                  <tr>
                    <th className="text-left py-2">Date · time</th>
                    <th className="text-left py-2">Method</th>
                    <th className="text-left py-2">Status</th>
                    <th className="text-left py-2">Reference</th>
                    <th className="text-right py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((t) => {
                    const s = t.payment_status || "paid";
                    const cls = s === "paid" ? "bg-[#2C4C3B] text-white" : s === "pending" ? "bg-[#EADCC8] text-[#2C4C3B]" : "bg-[#C86650] text-white";
                    return (
                      <tr key={t.id} className="border-b border-[#EADCC8]" data-testid={`tx-${t.id}`}>
                        <td className="py-2 whitespace-nowrap">
                          {t.created_at ? (
                            <>
                              <div>{new Date(t.created_at).toLocaleDateString()}</div>
                              <div className="text-[10px] text-[#8A9490]">{new Date(t.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
                            </>
                          ) : "—"}
                        </td>
                        <td className="py-2 text-xs">{
                          t.method === "check" ? `Check${t.check_number ? " #" + t.check_number : ""}`
                          : t.method === "cash" ? "Cash"
                          : t.method === "money_order" ? "Money order"
                          : t.method === "bank_transfer" ? "Bank transfer"
                          : t.method === "settlement_credit" || t.lookup_key === "settlement_credit" ? "Settlement credit"
                          : t.mode === "manual" ? "Manual entry"
                          : (t.mode === "payment" || t.mode === "subscription" || t.mode === "subscription_renewal") ? "Credit card · Stripe"
                          : (t.method || t.mode || "—")
                        }</td>
                        <td className="py-2"><span className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 ${cls}`}>{s === "canceled" ? "Cancelled" : s}</span></td>
                        <td className="py-2 text-xs text-[#5C6A64]">{t.reference || t.lookup_key || "—"}</td>
                        <td className="py-2 text-right text-[#2C4C3B]">${((t.amount || 0) / 100).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}




// ---------- Board Tab ----------
function BoardTitleEditor({ user, onSaved }) {
  const PRESETS = ["", "President", "Vice President", "Secretary", "Treasurer", "Architectural Review", "Landscaping Chair", "Events Chair", "Board Member"];
  const startsCustom = user.board_title && !PRESETS.includes(user.board_title);
  const [selection, setSelection] = useState(startsCustom ? "__custom__" : (user.board_title || ""));
  const [customText, setCustomText] = useState(startsCustom ? user.board_title : "");
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  const effectiveTitle = selection === "__custom__" ? customText.trim() : selection;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/board-profile`, { board_title: effectiveTitle });
      toast.success(`${user.name} is now ${effectiveTitle || "Board Member"}`);
      setDirty(false);
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex gap-1 items-center flex-wrap">
      <select
        data-testid={`board-title-select-${user.id}`}
        value={selection}
        onChange={(e) => { setSelection(e.target.value); setDirty(true); }}
        className="text-sm border border-[#D5CFC4] rounded-full px-3 py-1.5 bg-white flex-1 min-w-0"
      >
        {PRESETS.map((o) => <option key={o || "none"} value={o}>{o || "— (unassigned)"}</option>)}
        <option value="__custom__">Custom…</option>
      </select>
      {selection === "__custom__" && (
        <Input
          data-testid={`board-title-custom-${user.id}`}
          value={customText}
          onChange={(e) => { setCustomText(e.target.value); setDirty(true); }}
          placeholder="Enter custom title"
          className="h-8 w-40 text-sm rounded-full"
        />
      )}
      {dirty && (
        <Button size="sm" onClick={save} disabled={busy || (selection === "__custom__" && !customText.trim())} data-testid={`board-title-save-${user.id}`} className="rounded-full bg-[#2C4C3B] h-8 px-3 text-xs">
          {busy ? "…" : "Save"}
        </Button>
      )}
    </div>
  );
}


function BoardTab({ users, properties, onReload }) {
  const [assigningUser, setAssigningUser] = useState(null); // user we're assigning a property to
  const setRole = async (userId, role) => {
    try {
      await api.patch(`/admin/users/${userId}/role`, { role });
      toast.success(role === "admin" ? "Promoted to admin" : "Demoted to member");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const remove = async (userId) => {
    if (!window.confirm("Delete this user account permanently?")) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("User deleted");
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const admins = users.filter((u) => u.role === "admin");
  const members = users.filter((u) => u.role !== "admin");

  return (
    <div className="mt-4 space-y-8">
      <div>
        <h3 className="font-serif text-2xl text-[#1A2421] mb-3 flex items-center gap-2">
          <Shield className="w-5 h-5 text-[#C86650]" /> Board Admins ({admins.length})
        </h3>
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#EADCC8] text-[#2C4C3B]">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3 w-[220px]">Board role</th>
                  <th className="text-left px-4 py-3">Since</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((u) => (
                  <tr key={u.id} data-testid={`admin-row-${u.id}`} className="border-t border-[#D5CFC4]">
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3"><BoardTitleEditor user={u} onSaved={onReload} /></td>
                    <td className="px-4 py-3 text-xs text-[#5C6A64]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => setRole(u.id, "member")} data-testid={`demote-${u.id}`} className="rounded-full border-[#C86650] text-[#C86650]">
                        Demote to member
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <div>
        <h3 className="font-serif text-2xl text-[#1A2421] mb-3">Members ({members.length})</h3>
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#EADCC8] text-[#2C4C3B]">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Unit</th>
                  <th className="text-left px-4 py-3">Since</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-6 text-[#5C6A64]">No members registered yet.</td></tr>
                )}
                {members.map((u) => {
                  const linkedProp = (properties || []).find((p) => p.linked_user_id === u.id || p.owner_user_id === u.id);
                  return (
                  <tr key={u.id} data-testid={`member-row-${u.id}`} className="border-t border-[#D5CFC4]">
                    <td className="px-4 py-3 font-medium">{u.name}</td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6A64]">{linkedProp ? <Link to={`/admin/properties/${linkedProp.id}`} className="text-[#2C4C3B] hover:underline">{linkedProp.address}</Link> : (u.unit_number || "—")}</td>
                    <td className="px-4 py-3 text-xs text-[#5C6A64]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex gap-2 justify-end">
                        {!linkedProp && (
                          <Button size="sm" variant="outline" data-testid={`assign-property-btn-${u.id}`} onClick={() => setAssigningUser(u)} className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white h-8 px-3 text-xs">
                            <Home className="w-3 h-3 mr-1" /> Assign property
                          </Button>
                        )}
                        <Button size="sm" onClick={() => setRole(u.id, "admin")} data-testid={`promote-${u.id}`} className="rounded-full bg-[#2C4C3B]">
                          <Shield className="w-3 h-3 mr-1" /> Promote
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(u.id)} data-testid={`remove-user-${u.id}`}>
                          <Trash2 className="w-4 h-4 text-[#C86650]" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
      <AssignPropertyToMemberDialog open={!!assigningUser} onOpenChange={(o) => !o && setAssigningUser(null)} user={assigningUser} properties={properties} onDone={onReload} />
    </div>
  );
}




function PropertyDialog({ open, onOpenChange, initial, onDone }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial || { address: "", unit_number: "", owner_name: "", owner_email: "", annual_assessment: 1200, starting_balance: 0, notes: "" });
  useEffect(() => { if (initial) setForm(initial); }, [initial]);

  const save = async () => {
    try {
      const body = { ...form, annual_assessment: parseFloat(form.annual_assessment) || 0, starting_balance: parseFloat(form.starting_balance) || 0, owner_email: form.owner_email || null };
      if (isEdit) {
        await api.patch(`/admin/properties/${initial.id}`, body);
      } else {
        await api.post("/admin/properties", body);
      }
      toast.success(isEdit ? "Property updated" : "Property added");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">{isEdit ? "Edit Property" : "Add Property"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Unit #</label>
              <Input data-testid="prop-unit-input" value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Annual $</label>
              <Input data-testid="prop-annual-input" type="number" step="0.01" value={form.annual_assessment} onChange={(e) => setForm({ ...form, annual_assessment: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Address</label>
            <Input data-testid="prop-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Owner name</label>
            <Input data-testid="prop-owner-name-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Owner email</label>
            <Input data-testid="prop-owner-email-input" type="email" value={form.owner_email || ""} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Starting balance carryover ($)</label>
            <Input data-testid="prop-starting-balance-input" type="number" step="0.01" value={form.starting_balance || 0} onChange={(e) => setForm({ ...form, starting_balance: e.target.value })} className="mt-1 rounded-xl" />
            <p className="text-xs text-[#5C6A64] mt-1">Prior-year unpaid amount, if any.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes</label>
            <Textarea data-testid="prop-notes-input" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <Button data-testid="prop-save-btn" onClick={save} className="w-full rounded-full bg-[#2C4C3B]">
            {isEdit ? "Save changes" : "Add property"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ImportCsvDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) return toast.error("Choose a CSV file");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/properties/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.inserted} new, updated ${data.updated}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Import Homeowners CSV</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#5C6A64]">
            Upload a CSV with columns: <code className="bg-[#EADCC8] px-1 rounded text-xs">address, unit_number, owner_name, owner_email, annual_assessment, starting_balance, notes</code>. Existing units are updated; new ones inserted.
          </p>
          <a href={`${BACKEND}/api/admin/properties/template`} target="_blank" rel="noreferrer" className="text-sm text-[#C86650] underline">Download template CSV</a>
          <input
            data-testid="import-file-input"
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => setFile(e.target.files?.[0])}
            className="block w-full text-sm text-[#1A2421] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-[#2C4C3B] file:text-white hover:file:bg-[#233D2F]"
          />
          {result && (
            <div className="bg-[#F9F8F6] rounded-xl p-4 text-sm">
              <p className="text-[#2C4C3B]"><b>Inserted:</b> {result.inserted} · <b>Updated:</b> {result.updated}</p>
              {result.errors?.length > 0 && (
                <div className="mt-2">
                  <p className="text-[#C86650] font-medium">Issues:</p>
                  <ul className="list-disc list-inside text-[#5C6A64] text-xs mt-1">
                    {result.errors.map((e, i) => <li key={`err-${i}-${String(e).slice(0, 40)}`}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
          <Button data-testid="import-submit-btn" onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2C4C3B]">
            {busy ? "Importing…" : "Import CSV"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Delinquency Tab ----------
function DelinquencyTab({ data, properties, onReload }) {
  const [deadline, setDeadline] = useState("");
  const [savingDeadline, setSavingDeadline] = useState(false);
  const [search, setSearch] = useState("");

  // Memoized visible delinquent list — filters data.properties by the search input.
  const visibleProperties = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data.properties || [];
    return (data.properties || []).filter((p) =>
      (p.address || "").toLowerCase().includes(q) ||
      (p.unit_number || "").toString().toLowerCase().includes(q) ||
      (p.owner_name || "").toLowerCase().includes(q) ||
      (p.owner_email || "").toLowerCase().includes(q)
    );
  }, [data.properties, search]);

  useEffect(() => {
    api.get("/admin/settings/settlement").then(({ data }) => setDeadline(data.settlement_deadline || ""));
  }, []);

  const saveDeadline = async () => {
    if (!deadline) return toast.error("Pick a date");
    setSavingDeadline(true);
    try {
      await api.patch("/admin/settings/settlement", { deadline_date: deadline });
      toast.success("Settlement letter deadline updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSavingDeadline(false); }
  };

  const sendReminder = async (id) => {
    try {
      const { data: r } = await api.post(`/admin/properties/${id}/remind`);
      toast.success(`Reminder sent (balance $${r.balance.toFixed(2)})`);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
  };

  return (
    <div className="mt-4">
      <Card className="rounded-2xl border-[#EADCC8] bg-[#FDF9F1] mb-6" data-testid="settlement-deadline-card">
        <CardContent className="p-5 flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[240px]">
            <p className="font-serif text-lg text-[#1A2421]">Settlement Letter Deadline</p>
            <p className="text-xs text-[#5C6A64] mt-1">Applied to every settlement offer generated from the delinquency list and property detail pages. Update this before mailing the next notice.</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Deadline</label>
            <Input data-testid="settlement-deadline-input" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="mt-1 rounded-xl h-10 w-44" />
          </div>
          <Button data-testid="save-settlement-deadline" onClick={saveDeadline} disabled={savingDeadline} className="rounded-full bg-[#2C4C3B]">
            {savingDeadline ? "Saving…" : "Save deadline"}
          </Button>
          <LetterTemplateDialog />
          <BulkLettersDialog delinquentCount={data.properties.length} withEmailCount={data.properties.filter((p) => p.owner_email).length} onDone={onReload} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="rounded-2xl border-[#C86650] bg-[#FDF3F0]">
          <CardContent className="p-6">
            <AlertTriangle className="w-5 h-5 text-[#C86650] mb-2" />
            <p className="font-serif text-3xl text-[#C86650]">${(data.total_past_due ?? data.total_outstanding ?? 0).toLocaleString()}</p>
            <p className="text-xs uppercase tracking-wider text-[#5C6A64] mt-1">Past Due <span className="normal-case text-[10px]">(prior years — current year excluded)</span></p>
            {data.total_current_year_open > 0 && (
              <p className="text-[11px] text-[#5C6A64] mt-2">Plus ${data.total_current_year_open.toLocaleString()} in current-year dues still open on these accounts</p>
            )}
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardContent className="p-6">
            <p className="font-serif text-3xl text-[#2C4C3B]">{data.delinquent_count}</p>
            <p className="text-xs uppercase tracking-wider text-[#5C6A64] mt-1">Fully Delinquent</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardContent className="p-6">
            <p className="font-serif text-3xl text-[#2C4C3B]">{data.partial_count}</p>
            <p className="text-xs uppercase tracking-wider text-[#5C6A64] mt-1">Partial Payment</p>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl border-[#D5CFC4]">
        <CardContent className="p-0 overflow-x-auto">
          <div className="p-4 border-b border-[#EADCC8] flex items-center gap-3 flex-wrap">
            <Input
              data-testid="delinquency-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search address, unit, owner, email…"
              className="w-72 rounded-full bg-white h-9"
            />
            <p className="text-xs text-[#5C6A64]">
              {(() => {
                const q = search.trim().toLowerCase();
                if (!q) return `${data.properties.length} past-due`;
                const n = data.properties.filter((p) =>
                  (p.address || "").toLowerCase().includes(q) ||
                  (p.unit_number || "").toString().toLowerCase().includes(q) ||
                  (p.owner_name || "").toLowerCase().includes(q) ||
                  (p.owner_email || "").toLowerCase().includes(q)
                ).length;
                return `${n} of ${data.properties.length}`;
              })()}
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-[#EADCC8] text-[#2C4C3B]">
              <tr>
                <th className="text-left px-4 py-3">Address</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-right px-4 py-3">Annual</th>
                <th className="text-right px-4 py-3">Paid YTD</th>
                <th className="text-right px-4 py-3">This Year Due</th>
                <th className="text-right px-4 py-3">Past Due</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleProperties
                .map((p) => (
                <tr key={p.id} data-testid={`delinquent-row-${p.id}`} className="border-t border-[#D5CFC4]">
                  <td className="px-4 py-3">
                    <Link to={`/admin/properties/${p.id}`} className="font-medium text-[#1A2421] hover:text-[#2C4C3B] hover:underline" data-testid={`del-prop-link-${p.id}`}>{p.address}</Link>
                    <p className="text-xs text-[#5C6A64]">Unit {p.unit_number}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p>{p.owner_name}</p>
                    {p.owner_email && <p className="text-xs text-[#5C6A64]">{p.owner_email}</p>}
                  </td>
                  <td className="px-4 py-3 text-right">${p.annual_assessment.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-[#2C4C3B]">${p.ytd_paid.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-[#5C6A64]">${(p.current_year_due ?? 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-[#C86650] font-medium">${(p.past_due ?? p.balance).toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <Badge className={p.status === "partial" ? "bg-[#EADCC8] text-[#2C4C3B]" : "bg-[#C86650]"}>
                      {p.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-1 justify-end">
                      <ArrangementDialog property={properties?.find((x) => x.id === p.id) || p} defaultAmount={p.balance} onDone={onReload} />
                      <Button
                        size="sm"
                        onClick={() => sendReminder(p.id)}
                        disabled={!p.owner_email}
                        data-testid={`remind-${p.id}`}
                        className="rounded-full bg-[#2C4C3B] hover:bg-[#233D2F]"
                      >
                        <MailWarning className="w-4 h-4 mr-1" /> Remind
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.properties.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-[#2C4C3B]">All properties are current. 🌿</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------- Manual Payment Dialog ----------
function ManualPaymentDialog({ properties, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    property_id: "",
    amount: "",
    method: "check",
    reference: "",
    lookup_key: "annual_dues",
    payment_date: new Date().toISOString().split("T")[0],
    notes: "",
    send_receipt: true,
    apply_settlement: "",  // "" | "option1_65_paid_in_full" | "option2_50_payment_plan"
    settlement_installments: 6,
    settlement_monthly_amount: "",
    settlement_first_due_date: new Date(Date.now() + 14 * 86400 * 1000).toISOString().slice(0, 10),
    settlement_frequency: "monthly",
  });
  const [busy, setBusy] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const [lastPosted, setLastPosted] = useState(null); // {amount, method} to confirm previous save

  const submit = async () => {
    if (!form.property_id || !form.amount) return toast.error("Property and amount required");
    setBusy(true);
    try {
      const body = {
        property_id: form.property_id,
        amount: parseFloat(form.amount),
        method: form.method,
        reference: form.reference,
        lookup_key: form.lookup_key,
        payment_date: form.payment_date,
        notes: form.notes,
        send_receipt: form.send_receipt,
      };
      if (form.apply_settlement) {
        body.apply_settlement = form.apply_settlement;
        if (form.apply_settlement === "option2_50_payment_plan") {
          const monthly = parseFloat(form.settlement_monthly_amount);
          if (!isNaN(monthly) && monthly > 0) body.settlement_monthly_amount = monthly;
          else body.settlement_installments = parseInt(form.settlement_installments, 10);
          body.settlement_first_due_date = form.settlement_first_due_date;
          body.settlement_frequency = form.settlement_frequency;
        }
      }
      const { data } = await api.post("/admin/payments/manual", body);
      if (data.settlement_applied) {
        toast.success(`Payment posted · Settlement applied (discount $${data.settlement_applied.discount.toFixed(2)})`);
      } else {
        toast.success(form.send_receipt ? "Payment posted & receipt emailed" : "Payment posted");
      }
      const postedAmount = parseFloat(form.amount);
      const postedMethod = form.method;
      onDone();
      if (keepOpen) {
        // Keep property_id + method/lookup so admin can post multiple entries for the same account fast.
        setLastPosted({ amount: postedAmount, method: postedMethod });
        setForm((f) => ({
          ...f,
          amount: "",
          reference: "",
          notes: "",
          apply_settlement: "",
        }));
      } else {
        setOpen(false);
        setLastPosted(null);
        setForm((f) => ({ ...f, amount: "", reference: "", notes: "", apply_settlement: "" }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to post");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="post-payment-btn" className="rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white">
          <Plus className="w-4 h-4 mr-2" /> Post Check Payment
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Post Offline Payment</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Property</label>
            <PropertyPicker
              properties={properties}
              value={form.property_id}
              onChange={(v) => setForm({ ...form, property_id: v })}
              testidPrefix="manual-property"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Amount ($)</label>
              <Input data-testid="manual-amount-input" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Payment date</label>
              <Input data-testid="manual-date-input" type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Method</label>
              <Select
                value={STANDARD_METHODS.has(form.method) ? form.method : "custom"}
                onValueChange={(v) => {
                  if (v === "custom") {
                    // Preserve any prior custom text if the user re-selects "custom"
                    setForm({ ...form, method: form._custom_method || "" });
                  } else {
                    setForm({ ...form, method: v });
                  }
                }}
              >
                <SelectTrigger data-testid="manual-method-select" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="money_order">Money order</SelectItem>
                  <SelectItem value="custom">Custom…</SelectItem>
                </SelectContent>
              </Select>
              {!STANDARD_METHODS.has(form.method) && (
                <Input
                  data-testid="manual-method-custom"
                  placeholder="Type custom method (e.g., zelle, venmo, wire)"
                  value={form.method}
                  onChange={(e) => setForm({ ...form, method: e.target.value, _custom_method: e.target.value })}
                  className="mt-2 rounded-xl"
                />
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Reference / Check #</label>
              <Input data-testid="manual-reference-input" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Applied to</label>
            <Select value={form.lookup_key} onValueChange={(v) => setForm({ ...form, lookup_key: v })}>
              <SelectTrigger data-testid="manual-lookup-select" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="annual_dues">Annual Dues</SelectItem>
                <SelectItem value="quarterly_dues">Quarterly Dues</SelectItem>
                <SelectItem value="special_assessment">Special Assessment</SelectItem>
                <SelectItem value="late_fee">Late Fee</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes</label>
            <Textarea data-testid="manual-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" rows={2} />
          </div>
          <label className="flex items-start gap-2 text-sm text-[#1A2421] cursor-pointer p-3 bg-[#FDF9F1] border border-[#EADCC8] rounded-xl" data-testid="manual-send-receipt-label">
            <input
              type="checkbox"
              checked={form.send_receipt}
              onChange={(e) => setForm({ ...form, send_receipt: e.target.checked })}
              data-testid="manual-send-receipt"
              className="mt-1"
            />
            <span>Email a receipt to the homeowner (requires an email on their property record).</span>
          </label>

          <div className="border-t border-[#EADCC8] pt-3">
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Apply a settlement offer (optional — for past-due only)</label>
            <Select value={form.apply_settlement || "none"} onValueChange={(v) => setForm({ ...form, apply_settlement: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="manual-settlement-select" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No — regular payment</SelectItem>
                <SelectItem value="option1_65_paid_in_full">Option 1 — Pay 35% of dues (65% off, paid in full)</SelectItem>
                <SelectItem value="option2_50_payment_plan">Option 2 — Pay 50% of dues (in full or via plan)</SelectItem>
              </SelectContent>
            </Select>
            {form.apply_settlement === "option1_65_paid_in_full" && (
              <p className="text-xs text-[#5C6A64] mt-2">The system will credit a 65% discount from the current balance and mark the account settled once this payment posts.</p>
            )}
            {form.apply_settlement === "option2_50_payment_plan" && (() => {
              const selectedProp = properties.find((pp) => pp.id === form.property_id);
              const bal = selectedProp?.balance ?? 0;
              const settledAmt = +(bal * 0.5).toFixed(2);
              const paidNow = parseFloat(form.amount) || 0;
              const remainder = Math.max(0, +(settledAmt - paidNow).toFixed(2));
              const needsPlan = remainder > 0.01;
              return (
                <>
                  {selectedProp && (
                    <div className="mt-2 p-3 rounded-xl bg-[#FDF9F1] border border-[#EADCC8] text-xs text-[#1A2421] space-y-1">
                      <div className="flex justify-between"><span>Current balance</span><b>${bal.toFixed(2)}</b></div>
                      <div className="flex justify-between text-[#2C4C3B]"><span>50% credit (settlement)</span><b>− ${settledAmt.toFixed(2)}</b></div>
                      <div className="flex justify-between border-t border-[#EADCC8] pt-1"><span><b>Amount to settle</b></span><b className="text-[#C86650]">${settledAmt.toFixed(2)}</b></div>
                      <div className="flex justify-between"><span>This payment</span><b>${paidNow.toFixed(2)}</b></div>
                      <div className="flex justify-between border-t border-[#EADCC8] pt-1"><span><b>{needsPlan ? "Auto-planned remainder" : "Fully settled"}</b></span><b className={needsPlan ? "text-[#C86650]" : "text-[#2C4C3B]"}>{needsPlan ? `$${remainder.toFixed(2)}` : "$0.00 ✓"}</b></div>
                    </div>
                  )}
                  <p className="text-xs text-[#5C6A64] mt-2 mb-2">
                    {needsPlan
                      ? "A payment plan will be automatically added to the homeowner's account to settle the remaining balance. Set the schedule below."
                      : "This payment covers the full settled amount (50% of dues). No payment plan will be created."}
                  </p>
                  {needsPlan && (
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Installments</label>
                        <Input data-testid="manual-settlement-installments" type="number" min="2" max="12" value={form.settlement_installments} onChange={(e) => setForm({ ...form, settlement_installments: e.target.value })} className="mt-1 rounded-xl" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Monthly ($)</label>
                        <Input data-testid="manual-settlement-monthly" type="number" step="0.01" value={form.settlement_monthly_amount} onChange={(e) => setForm({ ...form, settlement_monthly_amount: e.target.value })} className="mt-1 rounded-xl" placeholder="override" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#5C6A64]">First due</label>
                        <Input data-testid="manual-settlement-first-due" type="date" value={form.settlement_first_due_date} onChange={(e) => setForm({ ...form, settlement_first_due_date: e.target.value })} className="mt-1 rounded-xl" />
                      </div>
                      <div>
                        <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Frequency</label>
                        <Select value={form.settlement_frequency} onValueChange={(v) => setForm({ ...form, settlement_frequency: v })}>
                          <SelectTrigger data-testid="manual-settlement-frequency" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="bi-weekly">Bi-weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <label className="flex items-start gap-2 text-sm text-[#1A2421] cursor-pointer p-3 bg-[#EADCC8] border border-[#D5CFC4] rounded-xl" data-testid="manual-keep-open-label">
            <input
              type="checkbox"
              checked={keepOpen}
              onChange={(e) => setKeepOpen(e.target.checked)}
              data-testid="manual-keep-open"
              className="mt-1"
            />
            <span>
              <b>Post another payment for this account</b> after saving. Keeps the property selected so you can enter multiple checks (e.g. arrears + current dues) in one sitting.
            </span>
          </label>

          {lastPosted && keepOpen && (
            <div className="p-3 rounded-xl bg-[#2C4C3B] text-white text-sm" data-testid="manual-last-posted">
              ✓ Last posted: <b>${lastPosted.amount.toFixed(2)}</b> via {lastPosted.method}. Enter the next payment below.
            </div>
          )}

          <Button data-testid="manual-save-btn" onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2C4C3B]">
            {busy ? "Posting…" : (keepOpen ? "Post payment & keep open" : "Post payment")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Mark Deposit Paid Dialog ----------
function MarkDepositPaidDialog({ booking, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    amount: (booking.deposit_required || 300).toString(),
    method: "check",
    reference: "",
    notes: "",
  });

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/deposit-status`, {
        paid: true,
        method: form.method,
        reference: form.reference,
        amount: parseFloat(form.amount),
        notes: form.notes,
      });
      toast.success("Deposit marked paid");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          data-testid={`mark-deposit-paid-${booking.id}`}
          className="rounded-full border-[#2C4C3B] text-[#2C4C3B]"
        >
          <CheckCircle2 className="w-3 h-3 mr-1" /> Mark deposit paid
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Mark Deposit Paid</DialogTitle></DialogHeader>
        <p className="text-sm text-[#5C6A64] -mt-2">
          {booking.amenity_name || booking.amenity} · {booking.date} · {booking.user_name || "—"}
        </p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Amount ($)</label>
              <Input data-testid={`deposit-amount-${booking.id}`} type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Method</label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid={`deposit-method-${booking.id}`} className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="money_order">Money order</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Reference / Check #</label>
            <Input data-testid={`deposit-reference-${booking.id}`} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes (optional)</label>
            <Textarea data-testid={`deposit-notes-${booking.id}`} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <Button data-testid={`deposit-save-${booking.id}`} onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2C4C3B]">
            {busy ? "Saving…" : "Mark deposit paid"}
          </Button>
          <p className="text-xs text-[#5C6A64] text-center">This records a manual `amenity_deposit` transaction in Payments.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Edit Payment Dialog (manual/offline only) ----------
function EditPaymentDialog({ payment, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(() => ({
    amount: ((payment.amount || 0) / 100).toString(),
    method: payment.method || "check",
    reference: payment.reference || "",
    lookup_key: payment.lookup_key || "manual_check",
    notes: payment.notes || "",
    payment_date: (payment.created_at || "").slice(0, 10),
  }));

  const submit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Amount is required");
    setBusy(true);
    try {
      await api.patch(`/admin/payments/${payment.id}`, {
        amount: parseFloat(form.amount),
        method: form.method,
        reference: form.reference,
        lookup_key: form.lookup_key,
        notes: form.notes,
        payment_date: form.payment_date,
      });
      toast.success("Payment updated");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          data-testid={`edit-payment-${payment.id}`}
          title="Edit manual payment"
        >
          <Pencil className="w-4 h-4 text-[#2C4C3B]" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit Offline Payment</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#5C6A64] -mt-2">
          {payment.user_name || "—"} · {payment.user_email || "no email"} · original ${((payment.amount || 0) / 100).toFixed(2)}
        </p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Amount ($)</label>
              <Input data-testid={`edit-amount-${payment.id}`} type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Payment date</label>
              <Input data-testid={`edit-date-${payment.id}`} type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Method</label>
              <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                <SelectTrigger data-testid={`edit-method-${payment.id}`} className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="money_order">Money order</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Reference / Check #</label>
              <Input data-testid={`edit-reference-${payment.id}`} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Applied to</label>
            <Select value={form.lookup_key} onValueChange={(v) => setForm({ ...form, lookup_key: v })}>
              <SelectTrigger data-testid={`edit-lookup-${payment.id}`} className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="annual_dues">Annual Dues</SelectItem>
                <SelectItem value="quarterly_dues">Quarterly Dues</SelectItem>
                <SelectItem value="special_assessment">Special Assessment</SelectItem>
                <SelectItem value="late_fee">Late Fee</SelectItem>
                <SelectItem value="settlement_deposit">Settlement Deposit</SelectItem>
                <SelectItem value="settlement_credit">Settlement Credit</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes</label>
            <Textarea data-testid={`edit-notes-${payment.id}`} rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <Button data-testid={`edit-save-${payment.id}`} onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2C4C3B]">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Manual Booking Dialog ----------
function ManualBookingDialog({ properties, amenities, onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    amenity: "",
    date: "",
    time_slot: "",
    guests: 1,
    property_id: "",
    homeowner_name: "",
    homeowner_email: "",
    homeowner_phone: "",
    notes: "",
    send_deposit_email: true,
  });

  const SLOTS = ["Half day — Morning (8 AM – 1 PM)", "Half day — Afternoon (2 PM – 8 PM)", "All day (8 AM – 10 PM)"];

  const onPropertyChange = (v) => {
    const p = properties.find((x) => x.id === v);
    setForm({
      ...form,
      property_id: v,
      homeowner_name: form.homeowner_name || p?.owner_name || "",
      homeowner_email: form.homeowner_email || p?.owner_email || "",
    });
  };

  const submit = async () => {
    if (!form.amenity || !form.date || !form.time_slot || !form.homeowner_name) {
      return toast.error("Amenity, date, time slot, and homeowner name are required.");
    }
    setBusy(true);
    try {
      const payload = { ...form, guests: parseInt(form.guests, 10) || 1 };
      // Strip empty email so backend Optional[EmailStr] accepts it
      if (!payload.homeowner_email) delete payload.homeowner_email;
      // Can only email a deposit link if we actually have an email
      if (!form.homeowner_email) payload.send_deposit_email = false;
      await api.post("/admin/bookings", payload);
      toast.success(payload.send_deposit_email ? "Booking created and deposit email sent" : "Booking created");
      setOpen(false);
      setForm({ amenity: "", date: "", time_slot: "", guests: 1, property_id: "", homeowner_name: "", homeowner_email: "", homeowner_phone: "", notes: "", send_deposit_email: true });
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create booking");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-manual-booking-btn" className="rounded-full bg-[#C86650] hover:bg-[#B4553F]"><Plus className="w-4 h-4 mr-2" />Add manual booking</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Manual Booking</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#5C6A64]">For homeowners who requested a booking offline (email, phone, in person). Optionally emails them a Stripe deposit link.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Amenity</label>
              <Select value={form.amenity} onValueChange={(v) => setForm({ ...form, amenity: v })}>
                <SelectTrigger data-testid="mb-amenity" className="mt-1 rounded-xl"><SelectValue placeholder="Choose amenity" /></SelectTrigger>
                <SelectContent>
                  {amenities.map((a) => (<SelectItem key={a.key} value={a.key}>{a.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Date</label>
              <Input data-testid="mb-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Time slot</label>
            <Select value={form.time_slot} onValueChange={(v) => setForm({ ...form, time_slot: v })}>
              <SelectTrigger data-testid="mb-slot" className="mt-1 rounded-xl"><SelectValue placeholder="Choose slot" /></SelectTrigger>
              <SelectContent>
                {SLOTS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Property (optional — autofills homeowner)</label>
            <Select value={form.property_id} onValueChange={onPropertyChange}>
              <SelectTrigger data-testid="mb-property" className="mt-1 rounded-xl"><SelectValue placeholder="No linked property" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => (<SelectItem key={p.id} value={p.id}>{p.address} — Unit {p.unit_number} · {p.owner_name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Homeowner name</label>
              <Input data-testid="mb-name" value={form.homeowner_name} onChange={(e) => setForm({ ...form, homeowner_name: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Homeowner email (optional)</label>
              <Input data-testid="mb-email" type="email" value={form.homeowner_email} onChange={(e) => setForm({ ...form, homeowner_email: e.target.value })} className="mt-1 rounded-xl" placeholder="Leave blank if unknown" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Phone (optional)</label>
              <Input data-testid="mb-phone" value={form.homeowner_phone} onChange={(e) => setForm({ ...form, homeowner_phone: e.target.value })} className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Est. # of guests</label>
              <Input data-testid="mb-guests" type="number" min="1" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes (optional)</label>
            <Textarea data-testid="mb-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <label className={`flex items-start gap-2 text-sm cursor-pointer p-3 border rounded-xl ${form.homeowner_email ? "bg-[#FDF9F1] border-[#EADCC8] text-[#1A2421]" : "bg-[#F2EFE8] border-[#D5CFC4] text-[#5C6A64] cursor-not-allowed"}`}>
            <input type="checkbox" checked={!!form.homeowner_email && form.send_deposit_email} disabled={!form.homeowner_email} onChange={(e) => setForm({ ...form, send_deposit_email: e.target.checked })} data-testid="mb-send-deposit" className="mt-1" />
            <span>{form.homeowner_email ? "Email the homeowner a Stripe $300 deposit payment link to confirm the reservation." : "Add an email above to enable the Stripe $300 deposit payment link."}</span>
          </label>
          <Button data-testid="mb-save" onClick={submit} disabled={busy} className="w-full rounded-full bg-[#2C4C3B]">
            {busy ? "Creating…" : "Create booking"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Letter Template Dialog (global template editor) ----------
function LetterTemplateDialog() {
  const [open, setOpen] = useState(false);
  const [tpl, setTpl] = useState("");
  const [defaultTpl, setDefaultTpl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/settings/letter-template").then(({ data }) => {
      setTpl(data.template);
      setDefaultTpl(data.default);
    });
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/settings/letter-template", { template: tpl });
      toast.success("Template saved — will be used for future letters");
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="edit-letter-template" variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B]">Edit letter template</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Settlement Letter Template</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#5C6A64]">
            Global default used when you click "Generate settlement letter PDF" on any property. Placeholders will be substituted per property: <code>{"{owner_name}"}</code>, <code>{"{address}"}</code>, <code>{"{unit_number}"}</code>, <code>{"{balance}"}</code>, <code>{"{discount1}"}</code>, <code>{"{discount2}"}</code>, <code>{"{amount_due_option1}"}</code>, <code>{"{amount_due_option2}"}</code>, <code>{"{deadline}"}</code>.
          </p>
          <Textarea data-testid="letter-template-body" rows={20} value={tpl} onChange={(e) => setTpl(e.target.value)} className="rounded-xl font-mono text-xs" />
          <div className="flex gap-3">
            <Button data-testid="save-letter-template" onClick={save} disabled={busy} className="flex-1 rounded-full bg-[#2C4C3B]">{busy ? "Saving…" : "Save template"}</Button>
            <Button variant="outline" onClick={() => setTpl(defaultTpl)} className="rounded-full border-[#D5CFC4]">Reset to default</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Bulk Letters Dialog (Delinquency tab header) ----------
function BulkLettersDialog({ delinquentCount, withEmailCount, onDone }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("zip"); // "zip" | "email"
  const [busy, setBusy] = useState(false);
  const [deadlineOverride, setDeadlineOverride] = useState("");
  const [useCustomBody, setUseCustomBody] = useState(false);
  const [customBody, setCustomBody] = useState("");
  const [defaultTpl, setDefaultTpl] = useState("");
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/settings/letter-template").then(({ data }) => {
      setDefaultTpl(data.template);
      setCustomBody(data.template);
    });
    setResult(null);
  }, [open]);

  const run = async () => {
    if (mode === "zip" && !window.confirm(`Generate ${delinquentCount} settlement letter PDFs as a single ZIP download?`)) return;
    if (mode === "email" && !window.confirm(`Email ${withEmailCount} homeowners their settlement letters with PDF attached? ${delinquentCount - withEmailCount} property${delinquentCount - withEmailCount === 1 ? "" : "ies"} without an email on file will be skipped.`)) return;
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        mode,
        deadline_override: deadlineOverride || null,
        body_override: useCustomBody && customBody !== defaultTpl ? customBody : null,
        save_as_template: saveAsTemplate && useCustomBody && customBody !== defaultTpl,
      };
      if (mode === "zip") {
        const res = await api.post("/admin/settlement/letters/bulk", payload, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `settlement_letters_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        const included = res.headers["x-letters-included"];
        toast.success(`ZIP downloaded — ${included || delinquentCount} letters + manifest.csv`);
        setResult({ mode: "zip", included: included || delinquentCount });
      } else {
        const { data } = await api.post("/admin/settlement/letters/bulk", payload);
        toast.success(`${data.sent} letters emailed${data.skipped_no_email ? ` · ${data.skipped_no_email} skipped (no email)` : ""}${data.failures?.length ? ` · ${data.failures.length} failed` : ""}`);
        setResult(data);
        onDone?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk letter run failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-bulk-letters" variant="outline" className="rounded-full border-[#C86650] text-[#C86650]">
          Bulk mail-merge letters
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Bulk Settlement Letters</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="p-3 rounded-xl bg-[#FDF9F1] border border-[#EADCC8] text-sm">
            <p><b>{delinquentCount}</b> delinquent propert{delinquentCount === 1 ? "y" : "ies"} with a past-due balance. <b>{withEmailCount}</b> have an email on file (eligible for email delivery).</p>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Delivery mode</label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="bulk-mode" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="zip">Download ZIP (all {delinquentCount} letters + manifest.csv)</SelectItem>
                <SelectItem value="email">Email all with email on file ({withEmailCount} homeowners)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Deadline override (optional — falls back to global)</label>
            <Input data-testid="bulk-deadline" type="date" value={deadlineOverride} onChange={(e) => setDeadlineOverride(e.target.value)} className="mt-1 rounded-xl w-52" />
          </div>
          <label className="flex items-start gap-2 text-sm text-[#1A2421] cursor-pointer p-3 bg-white border border-[#EADCC8] rounded-xl">
            <input type="checkbox" checked={useCustomBody} onChange={(e) => setUseCustomBody(e.target.checked)} data-testid="bulk-use-custom" className="mt-1" />
            <span>Override the letter body just for this batch (leave unchecked to use the saved template).</span>
          </label>
          {useCustomBody && (
            <>
              <Textarea data-testid="bulk-body" rows={12} value={customBody} onChange={(e) => setCustomBody(e.target.value)} className="rounded-xl font-mono text-xs" />
              <label className="flex items-start gap-2 text-sm text-[#1A2421] cursor-pointer">
                <input type="checkbox" checked={saveAsTemplate} onChange={(e) => setSaveAsTemplate(e.target.checked)} data-testid="bulk-save-tpl" className="mt-1" />
                <span>Also save these edits as the new default template.</span>
              </label>
            </>
          )}
          <Button data-testid="run-bulk-letters" onClick={run} disabled={busy || (mode === "email" && withEmailCount === 0)} className="w-full rounded-full bg-[#C86650] hover:bg-[#B4553F]">
            {busy ? "Working…" : mode === "zip" ? `Generate ZIP (${delinquentCount})` : `Email ${withEmailCount} letters`}
          </Button>
          {result && result.mode === "email" && (
            <div className="p-3 rounded-xl bg-[#F2EFE8] border border-[#D5CFC4] text-xs text-[#1A2421]">
              <p><b>Sent:</b> {result.sent} · <b>Skipped (no email):</b> {result.skipped_no_email} · <b>Failed:</b> {result.failures?.length || 0}</p>
              {result.failures?.length > 0 && (
                <ul className="mt-2 list-disc pl-4 text-[#C86650]">
                  {result.failures?.slice(0, 5).map((f, i) => <li key={`fail-${f.unit || i}-${f.email || ""}`}>Unit {f.unit} · {f.email}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Edit Booking Dialog ----------
function EditBookingDialog({ booking, amenities, properties = [], onClose, onDone }) {
  const [form, setForm] = useState({
    amenity: booking.amenity || "",
    date: booking.date || "",
    time_slot: booking.time_slot || "",
    guests: booking.guests ?? 1,
    notes: booking.notes || "",
    admin_notes: booking.admin_notes || "",
    property_id: booking.property_id || "",
    homeowner_name: booking.user_name || "",
    homeowner_email: booking.user_email || "",
    homeowner_phone: booking.user_phone || "",
  });
  const [busy, setBusy] = useState(false);
  const [showReattribute, setShowReattribute] = useState(false);
  const SLOTS = ["Half day — Morning (8 AM – 1 PM)", "Half day — Afternoon (2 PM – 8 PM)", "All day (8 AM – 10 PM)"];

  const onPropertyChange = (v) => {
    if (v === "__none__") {
      setForm({ ...form, property_id: "" });
      return;
    }
    const p = properties.find((x) => x.id === v);
    setForm({
      ...form,
      property_id: v,
      homeowner_name: p?.owner_name || form.homeowner_name,
      homeowner_email: p?.owner_email || form.homeowner_email,
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        amenity: form.amenity,
        date: form.date,
        time_slot: form.time_slot,
        guests: parseInt(form.guests, 10) || 1,
        notes: form.notes,
        admin_notes: form.admin_notes,
      };
      // Only send re-attribution fields if the admin changed them
      if (form.property_id !== (booking.property_id || "")) payload.property_id = form.property_id;
      if (form.homeowner_name !== (booking.user_name || "")) payload.homeowner_name = form.homeowner_name;
      if (form.homeowner_email !== (booking.user_email || "")) {
        payload.homeowner_email = form.homeowner_email || null;
      }
      if (form.homeowner_phone !== (booking.user_phone || "")) payload.homeowner_phone = form.homeowner_phone;
      await api.patch(`/admin/bookings/${booking.id}`, payload);
      toast.success("Booking updated · homeowner notified");
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Edit Booking</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#5C6A64]">Currently attributed to: <b>{booking.user_name || "—"}</b> {booking.user_email ? `(${booking.user_email})` : ""}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Amenity</label>
              <Select value={form.amenity} onValueChange={(v) => setForm({ ...form, amenity: v })}>
                <SelectTrigger data-testid="edit-bk-amenity" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {amenities.map((a) => (<SelectItem key={a.key} value={a.key}>{a.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Date</label>
              <Input data-testid="edit-bk-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Time slot</label>
            <Select value={form.time_slot} onValueChange={(v) => setForm({ ...form, time_slot: v })}>
              <SelectTrigger data-testid="edit-bk-slot" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{SLOTS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Est. # of guests</label>
            <Input data-testid="edit-bk-guests" type="number" min="1" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="mt-1 rounded-xl w-32" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Homeowner note</label>
            <Textarea data-testid="edit-bk-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Board note (included in the change email)</label>
            <Textarea data-testid="edit-bk-admin-notes" rows={2} value={form.admin_notes} onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} className="mt-1 rounded-xl" />
          </div>

          {/* Re-attribution section (collapsed by default) */}
          <div className="border-t border-[#EADCC8] pt-3">
            {!showReattribute ? (
              <button
                type="button"
                data-testid="edit-bk-reattribute-toggle"
                onClick={() => setShowReattribute(true)}
                className="text-sm text-[#C86650] hover:underline"
              >
                Change homeowner / property →
              </button>
            ) : (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wider text-[#C86650]">Re-attribute this booking</p>
                <p className="text-[11px] text-[#5C6A64] -mt-2">
                  Change the property or homeowner details. Selecting a property auto-fills name & email; homeowner will still see it in their portal if their account is linked to the new property.
                </p>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Property</label>
                  <Select value={form.property_id || "__none__"} onValueChange={onPropertyChange}>
                    <SelectTrigger data-testid="edit-bk-property" className="mt-1 rounded-xl"><SelectValue placeholder="Choose property…" /></SelectTrigger>
                    <SelectContent className="max-h-80">
                      <SelectItem value="__none__">— No property (unlinked) —</SelectItem>
                      {properties.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.address} · Unit {p.unit_number} — {p.owner_name || "(no name)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Homeowner name</label>
                    <Input data-testid="edit-bk-owner-name" value={form.homeowner_name} onChange={(e) => setForm({ ...form, homeowner_name: e.target.value })} className="mt-1 rounded-xl" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Homeowner email</label>
                    <Input data-testid="edit-bk-owner-email" type="email" value={form.homeowner_email} onChange={(e) => setForm({ ...form, homeowner_email: e.target.value })} className="mt-1 rounded-xl" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Homeowner phone (optional)</label>
                  <Input data-testid="edit-bk-owner-phone" value={form.homeowner_phone} onChange={(e) => setForm({ ...form, homeowner_phone: e.target.value })} className="mt-1 rounded-xl" />
                </div>
                <button
                  type="button"
                  onClick={() => setShowReattribute(false)}
                  className="text-xs text-[#5C6A64] hover:underline"
                >
                  Hide re-attribution fields
                </button>
              </div>
            )}
          </div>

          <Button data-testid="edit-bk-save" onClick={save} disabled={busy} className="w-full rounded-full bg-[#2C4C3B]">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}



/**
 * Shared "assign" dialog helpers used from the Properties tab (pick a MEMBER to link to a specific property)
 * and the Members tab (pick a PROPERTY to link to a specific member). Both wrap the same
 * POST /admin/properties/{prop_id}/assign endpoint.
 */

// From Properties tab: property is fixed, admin picks a member.
function AssignMemberToPropertyDialog({ open, onOpenChange, property, onDone }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/users")
      // Show ALL users in the picker — admins can also own homes (per policy), so we don't filter by role here.
      .then(({ data }) => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q ? users.filter((u) => (u.email || "").toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q)) : users;
    return base.slice(0, 50);
  }, [users, search]);

  const assign = async (userId, email) => {
    if (!window.confirm(`Assign ${email} to ${property?.address}? They'll gain homeowner-portal access to this unit.`)) return;
    setBusyId(userId);
    try {
      await api.post(`/admin/properties/${property.id}/assign`, { user_id: userId });
      toast.success(`Assigned ${email}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    } finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="assign-member-to-property-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1A2421]">Assign a member</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#5C6A64]">
            Pick a registered member to link to <b>{property?.address}</b>{property?.unit_number ? <> · Unit {property.unit_number}</> : null}.
          </p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6A64]" />
            <Input data-testid="assign-mtp-search" autoFocus placeholder="Search by name or email…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-xl h-11" />
          </div>
          <div className="max-h-72 overflow-y-auto border border-[#EADCC8] rounded-xl divide-y divide-[#EADCC8]" data-testid="assign-mtp-list">
            {loading && <p className="p-4 text-sm text-[#5C6A64]">Loading members…</p>}
            {!loading && filtered.length === 0 && <p className="p-4 text-sm text-[#5C6A64]">No members match "{search}"</p>}
            {!loading && filtered.map((u) => (
              <div key={u.id} className="p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1A2421] truncate">{u.name || "(no name)"}</p>
                  <p className="text-xs text-[#5C6A64] truncate">{u.email}</p>
                </div>
                <Button size="sm" data-testid={`assign-mtp-pick-${u.id}`} disabled={busyId === u.id} onClick={() => assign(u.id, u.email)} className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3D2E] text-white text-xs h-8 px-3 whitespace-nowrap">
                  {busyId === u.id ? "Assigning…" : "Assign"}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#5C6A64]">Members already linked to another property will be rejected. Unlink them there first.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// From Members tab: user is fixed, admin picks a property.
function AssignPropertyToMemberDialog({ open, onOpenChange, user, properties, onDone }) {
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const availableProps = React.useMemo(() => (properties || []).filter((p) => !p.linked_user_id && !p.owner_user_id), [properties]);
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = q
      ? availableProps.filter((p) =>
          (p.address || "").toLowerCase().includes(q) ||
          String(p.unit_number || "").toLowerCase().includes(q) ||
          (p.owner_name || "").toLowerCase().includes(q))
      : availableProps;
    return base.slice(0, 50);
  }, [availableProps, search]);

  const assign = async (propId, address) => {
    if (!window.confirm(`Assign ${address} to ${user?.email}? They'll gain homeowner-portal access to this unit.`)) return;
    setBusyId(propId);
    try {
      await api.post(`/admin/properties/${propId}/assign`, { user_id: user.id });
      toast.success(`${user.email} linked to ${address}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    } finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="assign-property-to-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1A2421]">Assign a property</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#5C6A64]">
            Pick an unclaimed property to link to <b>{user?.name || user?.email}</b>.
            <span className="text-xs block mt-1">Showing only unclaimed properties ({availableProps.length} available).</span>
          </p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6A64]" />
            <Input data-testid="assign-ptm-search" autoFocus placeholder="Search by address, unit, or previous owner…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-xl h-11" />
          </div>
          <div className="max-h-72 overflow-y-auto border border-[#EADCC8] rounded-xl divide-y divide-[#EADCC8]" data-testid="assign-ptm-list">
            {availableProps.length === 0 && <p className="p-4 text-sm text-[#5C6A64]">No unclaimed properties available.</p>}
            {availableProps.length > 0 && filtered.length === 0 && <p className="p-4 text-sm text-[#5C6A64]">No properties match "{search}"</p>}
            {filtered.map((p) => (
              <div key={p.id} className="p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1A2421] truncate">{p.address}</p>
                  <p className="text-xs text-[#5C6A64] truncate">
                    Unit {p.unit_number}{p.owner_name ? <> · previously: {p.owner_name}</> : null}
                  </p>
                </div>
                <Button size="sm" data-testid={`assign-ptm-pick-${p.id}`} disabled={busyId === p.id} onClick={() => assign(p.id, p.address)} className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3D2E] text-white text-xs h-8 px-3 whitespace-nowrap">
                  {busyId === p.id ? "Assigning…" : "Assign"}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Gmail reply dialog (sends via HOA Gmail SMTP with App Password) ----------
function GmailReplyDialog({ message, onDone }) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const openDialog = () => {
    setSubject(message.subject?.startsWith("Re:") ? message.subject : `Re: ${message.subject || "your message"}`);
    setBody("");
    setOpen(true);
  };

  const send = async () => {
    if (!body.trim()) { toast.error("Reply body is empty"); return; }
    setSending(true);
    try {
      const r = await api.post(`/contact/messages/${message.id}/reply`, { subject, body });
      toast.success(r.data.reply_to ? `Reply sent — homeowner's replies will land at ${r.data.reply_to}` : "Reply sent");
      setOpen(false);
      onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail || "Reply failed";
      toast.error(detail, { duration: 8000 });
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
      <Button size="sm" variant="outline" data-testid={`msg-reply-${message.id}`} onClick={openDialog} className="rounded-full border-[#2C4C3B] text-[#2C4C3B]">
        Reply
      </Button>
      <DialogContent className="bg-[#F5F1E8] max-w-2xl" data-testid={`reply-dialog-${message.id}`}>
        <DialogHeader>
          <DialogTitle className="text-[#1A2421]">Reply to {message.name || message.email}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-[#5C6A64]">
            To: <b className="text-[#1A2421]">{message.email}</b> · Sent via HOA email. Their reply will land in the HOA inbox automatically.
          </div>
          <div>
            <Label className="text-[#1A2421] text-sm">Subject</Label>
            <Input
              data-testid={`reply-subject-${message.id}`}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="bg-white border-[#D5CFC4]"
            />
          </div>
          <div>
            <Label className="text-[#1A2421] text-sm">Message</Label>
            <Textarea
              data-testid={`reply-body-${message.id}`}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder="Type your reply…"
              className="bg-white border-[#D5CFC4] font-sans"
            />
            <p className="text-[11px] text-[#5C6A64] mt-1">The homeowner's original message will be quoted below your reply automatically.</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#D5CFC4]">Cancel</Button>
            <Button
              data-testid={`reply-send-${message.id}`}
              onClick={send}
              disabled={sending || !body.trim()}
              className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3D2E] text-white"
            >
              {sending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Reconciliation PDF download (monthly treasurer report) ----------
function ReconciliationPdfButton() {
  const [open, setOpen] = useState(false);
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const download = () => {
    const url = `${BACKEND}/api/admin/reports/reconciliation.pdf?month=${encodeURIComponent(month)}`;
    // Include auth via a temporary anchor; api client's Authorization header can't be attached to a plain <a>,
    // so we fetch as a Blob (with the client) and click a synthetic download link.
    api.get(`/admin/reports/reconciliation.pdf?month=${encodeURIComponent(month)}`, { responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], { type: "application/pdf" });
        const objurl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objurl; a.download = `reconciliation_${month}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(objurl);
        setOpen(false);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Report generation failed"));
    return url;
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        data-testid="open-reconciliation-btn"
        variant="outline"
        className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white"
        onClick={() => setOpen(true)}
      >
        <FileText className="w-4 h-4 mr-2" /> Reconciliation PDF
      </Button>
      <DialogContent className="bg-[#F5F1E8] max-w-md" data-testid="reconciliation-dialog">
        <DialogHeader><DialogTitle className="text-[#1A2421]">Monthly reconciliation report</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#5C6A64]">
            Downloads a PDF listing every confirmed payment and every board-applied credit in the selected month.
            Use it to reconcile against your bank statement — the &quot;Total real cash-in&quot; figure should match
            Stripe deposits + manual check/cash entries.
          </p>
          <div>
            <Label className="text-sm text-[#1A2421]">Month</Label>
            <Input
              type="month"
              data-testid="reconciliation-month-input"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="bg-white border-[#D5CFC4]"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#D5CFC4]">Cancel</Button>
            <Button
              data-testid="reconciliation-download-btn"
              onClick={download}
              className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3D2E] text-white"
            >
              <Download className="w-4 h-4 mr-2" /> Download PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ---------- Edit payment method (correct Stripe → Check / Cash / etc without deleting the row) ----------
function EditMethodButton({ payment, onDone }) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState(payment.method || (payment.mode === "payment" ? "credit_card" : "check"));
  const [checkNumber, setCheckNumber] = useState(payment.check_number || "");
  const [reference, setReference] = useState(payment.reference || "");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/payments/${payment.id}/edit-method`, {
        method,
        check_number: method === "check" ? checkNumber : null,
        reference,
        convert_to_manual: method !== "credit_card",
      });
      toast.success("Payment method updated");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        size="icon"
        variant="ghost"
        data-testid={`edit-method-${payment.id}`}
        title="Edit payment method"
        onClick={() => setOpen(true)}
      >
        <Pencil className="w-4 h-4 text-[#2C4C3B]" />
      </Button>
      <DialogContent className="bg-[#F5F1E8] max-w-md" data-testid={`edit-method-dialog-${payment.id}`}>
        <DialogHeader>
          <DialogTitle className="text-[#1A2421]">Edit payment method</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-[#5C6A64]">
            <p><b className="text-[#1A2421]">{payment.user_name || payment.user_email}</b> · ${((payment.amount || 0) / 100).toFixed(2)} · {new Date(payment.created_at).toLocaleDateString()}</p>
            <p className="mt-1">Use this to correct a row that was recorded as Stripe but actually came in via check/cash. The paid amount stays applied to the homeowner's balance.</p>
          </div>
          <div>
            <Label className="text-[#1A2421] text-sm">Actual method</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid={`edit-method-select-${payment.id}`} className="border-[#D5CFC4] bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="check">Check</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="money_order">Money order</SelectItem>
                <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                <SelectItem value="credit_card">Credit card (keep as Stripe)</SelectItem>
                <SelectItem value="settlement_credit">Settlement credit (board applied)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {method === "check" && (
            <div>
              <Label className="text-[#1A2421] text-sm">Check number</Label>
              <Input
                data-testid={`edit-method-check-${payment.id}`}
                value={checkNumber}
                onChange={(e) => setCheckNumber(e.target.value)}
                placeholder="1234"
                className="bg-white border-[#D5CFC4]"
              />
            </div>
          )}
          <div>
            <Label className="text-[#1A2421] text-sm">Reference / note (optional)</Label>
            <Input
              data-testid={`edit-method-reference-${payment.id}`}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Any admin note about this payment…"
              className="bg-white border-[#D5CFC4]"
            />
          </div>
          {method !== "credit_card" && (
            <p className="text-[11px] text-[#5C6A64] italic">
              Choosing anything other than Credit card converts this row to a manual entry and clears the Stripe session / payment intent IDs so it stops looking like an online charge.
            </p>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-[#D5CFC4]">Cancel</Button>
            <Button
              data-testid={`edit-method-save-${payment.id}`}
              onClick={submit}
              disabled={busy}
              className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3D2E] text-white"
            >
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

